<template>
  <div class="flex flex-col items-center justify-center w-full bg-[#F2F6FA]">
    <section class="flex flex-col items-center w-full justify-center">
      <div
        class="flex items-center justify-start md:mt-5 mb-2 md:mb-0 mt-2 px-2 md:px-0 w-full"
      >
        <div class="items-center gap-2 px-4 mt-5 -ml-2 d-flex w-full">
          <img
            src="@/assets/img/sidebar/chl_n.svg"
            class="cursor-pointer w-6 md:w-[28px]"
            alt="post"
          />
          <div
            class="flex items-center max-sm:flex-col max-sm:gap-2 justify-between w-full"
          >
            <p class="text-base md:text-[22px] font-bold">Performance Stats</p>
            <div class="" v-if="showLoadingMessage">
              <div class="flex gap-2">
                <span v-if="lastFetchedDate">
                  Stats last fetched {{ lastFetchedDate }}, fetching latest..
                </span>
                <span v-else> Fetching latest data.. </span>
                <v-progress-circular
                  indeterminate
                  color="primary"
                  :size="18"
                  :width="3"
                ></v-progress-circular>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="w-full mx-auto mt-3">
        <div class="bg-white rounded-lg shadow-sm p-4 md:p-6">
          <!-- Screenshot Header (only visible during screenshot capture) -->
          <div
            class="screenshot-header"
            :class="{ hidden: !showScreenshotHeader }"
          >
            <div class="maze-pattern">
              <div class="header-content">
                <div class="workspace-info">
                  <img
                    v-if="currentWorkspaceLogo"
                    :src="currentWorkspaceLogo"
                    alt="Workspace Logo"
                    class="workspace-logo"
                  />
                  <span class="workspace-name">{{
                    currentWorkspaceName || "Workspace"
                  }}</span>
                </div>
                <div class="date-text">{{ currentDate }}</div>
              </div>
            </div>
          </div>
          <div
            v-if="!showScreenshotHeader"
            class="w-full items-center mx-auto px-4"
          >
            <div
              class="relative flex justify-between flex-col md:flex-row items-start gap-6 md:items-center md:gap-2 grow"
            >
              <div class="gap-2 w-full md:w-1/3">
                <div class="relative location-dropdown">
                  <v-btn
                    @click="toggleStoreDropdown($event)"
                    outlined
                    color="primary"
                  >
                    {{ selectedWIDs?.length || 0 }} Selected Locations
                  </v-btn>
                  <LocationDropdown
                    ref="showStoreDropdown"
                    :stores="filteredStores"
                    :showStoreDropdown="showStoreDropdown"
                    v-model="selectedWIDs"
                    @change="handleLocationChange"
                  />
                </div>
              </div>
              <div class="flex justify-end items-center gap-2">
                <v-select
                  v-model="selectedDate"
                  :items="dateOptions"
                  :label="
                    selectedDate?.isCustom
                      ? 'Custom Range Selected'
                      : 'Select Time Period'
                  "
                  dense
                  outlined
                  :loading="isLoadingDates"
                  :disabled="
                    isLoadingDates || isChartLoading || isAggregatedDataLoading
                  "
                  class="min-w-[200px] flex-1 sm:flex-none date-select"
                  hide-details
                  @change="selectDateRange"
                >
                  <template v-slot:selection="{ item }">
                    <span
                      v-if="selectedDate?.isCustom"
                      class="text-primary font-medium"
                    >
                      {{ selectedDate.text }}
                    </span>
                    <span v-else>
                      {{ item.text }}
                    </span>
                  </template>
                </v-select>

                <!-- Custom Date Range -->
                <v-dialog
                  v-model="showCustomDateRange"
                  max-width="480px"
                  persistent
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      outlined
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      :disabled="
                        isLoadingDates ||
                        isChartLoading ||
                        isAggregatedDataLoading
                      "
                      class="ml-2 text-xs"
                    >
                      <v-icon small class="mr-1">mdi-calendar-range</v-icon>
                      {{
                        selectedDate && selectedDate.isCustom
                          ? selectedDate.text
                          : "Custom Range"
                      }}
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-title class="text-lg font-medium">
                      <v-icon class="mr-2">mdi-calendar-range</v-icon>
                      Select Date Range
                      <v-spacer></v-spacer>
                      <v-btn
                        icon
                        @click="closeCustomDateRange"
                        :disabled="
                          isLoadingDates ||
                          isChartLoading ||
                          isAggregatedDataLoading
                        "
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text class="pb-2">
                      <v-row class="mx-0">
                        <v-col cols="12" class="pb-2">
                          <div class="text-sm text-gray-600 mb-3">
                            <span v-if="gmbStatsStartDate">
                              Data available from
                              {{
                                new Date(gmbStatsStartDate).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  }
                                )
                              }}.
                            </span>
                          </div>
                        </v-col>
                        <v-col cols="6" class="pr-2">
                          <v-text-field
                            v-model="customStartDate"
                            label="Start Date"
                            type="date"
                            outlined
                            dense
                            :min="minStartDate"
                            :max="maxStartDate"
                            :error="!!dateValidationError"
                            @change="validateCustomDates"
                            :disabled="
                              isLoadingDates ||
                              isChartLoading ||
                              isAggregatedDataLoading
                            "
                            prepend-inner-icon="mdi-calendar"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="6" class="pl-2">
                          <v-text-field
                            v-model="customEndDate"
                            label="End Date"
                            type="date"
                            outlined
                            dense
                            :min="customStartDate || minStartDate"
                            :max="maxEndDate"
                            :error="!!dateValidationError"
                            @change="validateCustomDates"
                            :disabled="
                              isLoadingDates ||
                              isChartLoading ||
                              isAggregatedDataLoading
                            "
                            prepend-inner-icon="mdi-calendar"
                          ></v-text-field>
                        </v-col>
                        <v-col
                          cols="12"
                          v-if="dateValidationError"
                          class="pt-0"
                        >
                          <div class="text-red-600 text-sm">
                            <v-icon small color="red" class="mr-1"
                              >mdi-alert-circle</v-icon
                            >
                            {{ dateValidationError }}
                          </div>
                        </v-col>
                      </v-row>

                      <!-- Quick Date Range Presets -->
                      <div class="mt-3">
                        <div class="text-sm font-medium text-gray-700 mb-2">
                          Quick Select:
                        </div>
                        <div class="flex flex-wrap gap-2">
                          <v-btn
                            x-small
                            outlined
                            @click="setQuickCustomRange(30)"
                            :disabled="
                              isLoadingDates ||
                              isChartLoading ||
                              isAggregatedDataLoading
                            "
                          >
                            Last 30 days
                          </v-btn>
                          <v-btn
                            x-small
                            outlined
                            @click="setQuickCustomRange(90)"
                            :disabled="
                              isLoadingDates ||
                              isChartLoading ||
                              isAggregatedDataLoading
                            "
                          >
                            Last 90 days
                          </v-btn>
                          <v-tooltip bottom :disabled="!isThisMonthDisabled">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                x-small
                                outlined
                                @click="setCurrentCustomMonth"
                                :disabled="
                                  isThisMonthDisabled ||
                                  isLoadingDates ||
                                  isChartLoading ||
                                  isAggregatedDataLoading
                                "
                                v-bind="attrs"
                                v-on="on"
                              >
                                This Month
                              </v-btn>
                            </template>
                            <span
                              >This month data not available - last 7 days data
                              not yet available</span
                            >
                          </v-tooltip>
                        </div>
                      </div>
                    </v-card-text>
                    <v-card-actions class="px-6 pb-4">
                      <v-btn
                        text
                        @click="clearCustomDateRange"
                        :disabled="
                          !isCustomDateRange ||
                          (!customStartDate && !customEndDate) ||
                          isLoadingDates ||
                          isChartLoading ||
                          isAggregatedDataLoading
                        "
                        color="error"
                      >
                        Clear
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        @click="applyCustomDateRange"
                        :disabled="!canApplyCustomDateRange"
                        :loading="
                          isLoadingDates ||
                          isChartLoading ||
                          isAggregatedDataLoading
                        "
                      >
                        Apply Filter
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
                <v-btn
                  color="primary"
                  icon
                  small
                  title="Save dashboard as image"
                  @click="captureScreenshot"
                >
                  <v-icon>mdi-download-box-outline</v-icon>
                </v-btn>
                <v-menu
                  v-model="showDownloadMenu"
                  offset-y
                  :close-on-content-click="false"
                  :disabled="isGeneratingReport"
                >
                  <template v-slot:activator="{ attrs }">
                    <v-btn
                      color="primary"
                      icon
                      small
                      title="Download report"
                      v-bind="attrs"
                      :loading="isGeneratingReport"
                      @click="generateReport"
                    >
                      <v-icon>mdi-file-download</v-icon>
                    </v-btn>
                  </template>

                  <v-list v-if="downloadUrls.excel_url || downloadUrls.csv_url">
                    <v-list-item
                      v-if="downloadUrls.excel_url"
                      @click="downloadFile('excel')"
                      class="cursor-pointer"
                    >
                      <v-list-item-icon>
                        <v-icon color="green">mdi-file-excel</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Download Excel</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                      v-if="downloadUrls.csv_url"
                      @click="downloadFile('csv')"
                      class="cursor-pointer"
                    >
                      <v-list-item-icon>
                        <v-icon color="blue">mdi-file-delimited</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Download CSV</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </div>

          <!-- Aggregated Data -->
          <div class="px-3 mx-2 mt-8 w-full">
            <!-- Performance Overview Card -->
            <div class="bg-white p-6 rounded-lg shadow-md mb-6">
              <h2 class="text-lg font-bold mb-4">Performance Overview</h2>
              <div
                v-if="isAggregatedDataLoading"
                class="flex justify-center items-center py-8"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                  :size="40"
                  :width="4"
                ></v-progress-circular>
              </div>
              <div
                v-else
                class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                <!-- Impressions -->
                <div class="bg-gray-50 p-4 rounded-lg">
                  <div class="flex items-center justify-between mb-2">
                    <h3 class="text-sm font-medium text-gray-600">
                      Total Impressions
                    </h3>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small v-bind="attrs" v-on="on" color="gray"
                          >mdi-information-outline</v-icon
                        >
                      </template>
                      <div
                        style="
                          max-width: 550px;
                          white-space: normal;
                          line-height: 1.4;
                        "
                      >
                        How many times your business showed up on Google Search
                        or Maps.<br />
                      </div>
                    </v-tooltip>
                  </div>
                  <div class="flex items-end gap-2">
                    <span class="text-2xl font-bold">{{
                      getImpressions()
                    }}</span>
                    <!-- <span class="text-sm text-gray-500">/100</span> -->
                    <div
                      v-if="
                        previousAggregatedData &&
                        Object.keys(previousAggregatedData).length > 0 &&
                        !previousMonthAggregatedDataLoading &&
                        shouldShowPreviousMonthData
                      "
                      :class="
                        getPercentageClass(
                          getImpressions(),
                          (previousAggregatedData.desktop_maps_impressions ||
                            0) +
                            (previousAggregatedData.mobile_maps_impressions ||
                              0) +
                            (previousAggregatedData.desktop_search_impressions ||
                              0) +
                            (previousAggregatedData.mobile_search_impressions ||
                              0)
                        )
                      "
                      class="text-xs flex items-center gap-1"
                    >
                      <v-icon small>{{
                        getPercentageIcon(
                          getImpressions(),
                          (previousAggregatedData.desktop_maps_impressions ||
                            0) +
                            (previousAggregatedData.mobile_maps_impressions ||
                              0) +
                            (previousAggregatedData.desktop_search_impressions ||
                              0) +
                            (previousAggregatedData.mobile_search_impressions ||
                              0)
                        )
                      }}</v-icon>
                      {{
                        calculatePercentageChange(
                          getImpressions(),
                          (previousAggregatedData.desktop_maps_impressions ||
                            0) +
                            (previousAggregatedData.mobile_maps_impressions ||
                              0) +
                            (previousAggregatedData.desktop_search_impressions ||
                              0) +
                            (previousAggregatedData.mobile_search_impressions ||
                              0)
                        )
                      }}%
                    </div>
                  </div>
                </div>

                <!-- Customer Actions -->
                <div class="bg-gray-50 p-4 rounded-lg">
                  <div class="flex items-center justify-between mb-2">
                    <h3 class="text-sm font-medium text-gray-600">
                      Customer Actions
                    </h3>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small v-bind="attrs" v-on="on" color="gray"
                          >mdi-information-outline</v-icon
                        >
                      </template>
                      <span
                        >Total number of customer interactions like direction
                        requests, call clicks, website clicks, and
                        bookings</span
                      >
                    </v-tooltip>
                  </div>
                  <div class="flex items-baseline gap-2">
                    <span class="text-2xl font-bold">{{
                      (aggregatedData.total_direction_requests || 0) +
                      (aggregatedData.total_call_clicks || 0) +
                      (aggregatedData.total_website_clicks || 0) +
                      (aggregatedData.total_bookings || 0)
                    }}</span>
                    <div
                      v-if="
                        previousAggregatedData &&
                        Object.keys(previousAggregatedData).length > 0 &&
                        !previousMonthAggregatedDataLoading &&
                        shouldShowPreviousMonthData
                      "
                      :class="
                        getPercentageClass(
                          (aggregatedData.total_direction_requests || 0) +
                            (aggregatedData.total_call_clicks || 0) +
                            (aggregatedData.total_website_clicks || 0) +
                            (aggregatedData.total_bookings || 0),
                          (previousAggregatedData.direction_requests || 0) +
                            (previousAggregatedData.call_clicks || 0) +
                            (previousAggregatedData.website_clicks || 0) +
                            (previousAggregatedData.bookings || 0)
                        )
                      "
                      class="text-xs flex items-center gap-1"
                    >
                      <v-icon small>{{
                        getPercentageIcon(
                          (aggregatedData.total_direction_requests || 0) +
                            (aggregatedData.total_call_clicks || 0) +
                            (aggregatedData.total_website_clicks || 0) +
                            (aggregatedData.total_bookings || 0),
                          (previousAggregatedData.direction_requests || 0) +
                            (previousAggregatedData.call_clicks || 0) +
                            (previousAggregatedData.website_clicks || 0) +
                            (previousAggregatedData.bookings || 0)
                        )
                      }}</v-icon>
                      {{
                        calculatePercentageChange(
                          (aggregatedData.total_direction_requests || 0) +
                            (aggregatedData.total_call_clicks || 0) +
                            (aggregatedData.total_website_clicks || 0) +
                            (aggregatedData.total_bookings || 0),
                          (previousAggregatedData.direction_requests || 0) +
                            (previousAggregatedData.call_clicks || 0) +
                            (previousAggregatedData.website_clicks || 0) +
                            (previousAggregatedData.bookings || 0)
                        )
                      }}%
                    </div>
                    <div
                      v-else-if="
                        previousMonthAggregatedDataLoading &&
                        shouldShowPreviousMonthData
                      "
                      class="text-xs"
                    >
                      <v-progress-circular
                        indeterminate
                        color="primary"
                        :size="14"
                        :width="2"
                      ></v-progress-circular>
                    </div>
                  </div>
                </div>

                <!-- Conversion Rate -->
                <div class="bg-gray-50 p-4 rounded-lg">
                  <div class="flex items-center justify-between mb-2">
                    <h3 class="text-sm font-medium text-gray-600">
                      Conversion Rate
                    </h3>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small v-bind="attrs" v-on="on" color="gray"
                          >mdi-information-outline</v-icon
                        >
                      </template>
                      <span
                        >Percentage of views that resulted in customer
                        actions</span
                      >
                    </v-tooltip>
                  </div>
                  <div class="flex items-end gap-2">
                    <span class="text-2xl font-bold">{{
                      Math.round(
                        ((aggregatedData.total_direction_requests +
                          aggregatedData.total_call_clicks +
                          aggregatedData.total_website_clicks +
                          aggregatedData.total_bookings) /
                          (aggregatedData.total_desktop_maps_impressions +
                            aggregatedData.total_mobile_maps_impressions +
                            aggregatedData.total_desktop_search_impressions +
                            aggregatedData.total_mobile_search_impressions)) *
                          100
                      ) || 0
                    }}</span>
                    <span class="text-sm text-gray-500">%</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Existing Metrics Cards -->
            <div class="bg-white p-4 pt-0 rounded-lg shadow-md mt-4 w-[98%]">
              <div class="overflow-x-auto">
                <div class="min-w-[1000px]">
                  <div class="flex justify-between">
                    <aggregated-metric-ui
                      :metricName="'Maps'"
                      :tooltipMessage="getTooltipMessage('maps', 'combined')"
                      :aggregatedData="
                        aggregatedData.total_desktop_maps_impressions +
                        aggregatedData.total_mobile_maps_impressions
                      "
                      :metricKey="'maps'"
                      :isLoading="isAggregatedDataLoading"
                      :previousMonthAggregatedDataLoading="
                        previousMonthAggregatedDataLoading
                      "
                      :previousMonthDataFetched="previousMonthDataFetched"
                      :selectedDate="selectedDate"
                      :metricsData="metricsData"
                      :chartData="chartData"
                      :metricData="
                        aggregatedData?.total_desktop_maps_impressions +
                        aggregatedData?.total_mobile_maps_impressions
                      "
                      :metricChange="getMetricChange('maps')"
                      :months="selectedDate.months"
                    />
                    <div class="border-l border-gray-300 mx-4"></div>
                    <aggregated-metric-ui
                      :metricName="'Search'"
                      :tooltipMessage="getTooltipMessage('search', 'combined')"
                      :aggregatedData="
                        aggregatedData?.total_desktop_search_impressions +
                        aggregatedData?.total_mobile_search_impressions
                      "
                      :metricKey="'search'"
                      :isLoading="isAggregatedDataLoading"
                      :previousMonthAggregatedDataLoading="
                        previousMonthAggregatedDataLoading
                      "
                      :previousMonthDataFetched="previousMonthDataFetched"
                      :selectedDate="selectedDate"
                      :metricsData="metricsData"
                      :chartData="chartData"
                      :metricData="
                        aggregatedData?.total_desktop_search_impressions +
                        aggregatedData?.total_mobile_search_impressions
                      "
                      :metricChange="getMetricChange('search')"
                      :months="selectedDate.months"
                    />
                    <div class="border-l border-gray-300 mx-4"></div>
                    <aggregated-metric-ui
                      :metricName="'Direction Requests'"
                      :tooltipMessage="
                        getTooltipMessage('directions', 'combined')
                      "
                      :aggregatedData="aggregatedData?.total_direction_requests"
                      :metricKey="'directions'"
                      :isLoading="isAggregatedDataLoading"
                      :previousMonthAggregatedDataLoading="
                        previousMonthAggregatedDataLoading
                      "
                      :previousMonthDataFetched="previousMonthDataFetched"
                      :selectedDate="selectedDate"
                      :metricsData="metricsData"
                      :chartData="chartData"
                      :metricData="aggregatedData?.total_direction_requests"
                      :metricChange="getMetricChange('directions')"
                      :months="selectedDate.months"
                    />
                    <div class="border-l border-gray-300 mx-4"></div>
                    <aggregated-metric-ui
                      :metricName="'Call Clicks'"
                      :tooltipMessage="getTooltipMessage('calls', 'combined')"
                      :aggregatedData="
                        aggregatedData?.total_call_clicks
                          ? aggregatedData?.total_call_clicks
                          : getTotalMetric('calls')
                      "
                      :metricKey="'calls'"
                      :isLoading="isAggregatedDataLoading"
                      :previousMonthAggregatedDataLoading="
                        previousMonthAggregatedDataLoading
                      "
                      :previousMonthDataFetched="previousMonthDataFetched"
                      :selectedDate="selectedDate"
                      :metricsData="metricsData"
                      :chartData="chartData"
                      :metricData="aggregatedData?.total_call_clicks"
                      :metricChange="getMetricChange('calls')"
                      :months="selectedDate.months"
                    />
                    <div class="border-l border-gray-300 mx-4"></div>
                    <aggregated-metric-ui
                      :metricName="'Website Clicks'"
                      :tooltipMessage="getTooltipMessage('website', 'combined')"
                      :aggregatedData="aggregatedData?.total_website_clicks"
                      :metricKey="'website'"
                      :isLoading="isAggregatedDataLoading"
                      :previousMonthAggregatedDataLoading="
                        previousMonthAggregatedDataLoading
                      "
                      :previousMonthDataFetched="previousMonthDataFetched"
                      :selectedDate="selectedDate"
                      :metricsData="metricsData"
                      :chartData="chartData"
                      :metricData="aggregatedData?.total_website_clicks"
                      :metricChange="getMetricChange('website')"
                      :months="selectedDate.months"
                    />
                    <div class="border-l border-gray-300 mx-4"></div>
                    <aggregated-metric-ui
                      :metricName="'Bookings'"
                      :tooltipMessage="
                        getTooltipMessage('bookings', 'combined')
                      "
                      :aggregatedData="aggregatedData?.total_bookings"
                      :metricKey="'bookings'"
                      :isLoading="isAggregatedDataLoading"
                      :previousMonthAggregatedDataLoading="
                        previousMonthAggregatedDataLoading
                      "
                      :previousMonthDataFetched="previousMonthDataFetched"
                      :selectedDate="selectedDate"
                      :metricsData="metricsData"
                      :chartData="chartData"
                      :metricData="aggregatedData?.total_bookings"
                      :metricChange="getMetricChange('bookings')"
                      :months="selectedDate.months"
                    />
                    <div class="border-l border-gray-300 mx-4"></div>
                    <aggregated-metric-ui
                      :metricName="'Food Orders'"
                      :tooltipMessage="
                        getTooltipMessage('food_orders', 'combined')
                      "
                      :aggregatedData="aggregatedData?.total_food_orders"
                      :metricKey="'food_orders'"
                      :isLoading="isAggregatedDataLoading"
                      :previousMonthAggregatedDataLoading="
                        previousMonthAggregatedDataLoading
                      "
                      :previousMonthDataFetched="previousMonthDataFetched"
                      :selectedDate="selectedDate"
                      :metricsData="metricsData"
                      :chartData="chartData"
                      :metricData="aggregatedData?.total_food_orders"
                      :metricChange="getMetricChange('food_orders')"
                      :months="selectedDate.months"
                    />
                    <div class="border-l border-gray-300 mx-4"></div>
                    <aggregated-metric-ui
                      :metricName="'Food Menu Clicks'"
                      :tooltipMessage="
                        getTooltipMessage('food_menu', 'combined')
                      "
                      :aggregatedData="aggregatedData?.total_food_menu_clicks"
                      :metricKey="'food_menu'"
                      :isLoading="isAggregatedDataLoading"
                      :previousMonthAggregatedDataLoading="
                        previousMonthAggregatedDataLoading
                      "
                      :previousMonthDataFetched="previousMonthDataFetched"
                      :selectedDate="selectedDate"
                      :metricsData="metricsData"
                      :chartData="chartData"
                      :metricData="aggregatedData?.total_food_menu_clicks"
                      :metricChange="getMetricChange('food_menu')"
                      :months="selectedDate.months"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="px-4 mt-2 mb-8 pt-4">
            <v-tabs
              v-model="tabIndex"
              background-color="transparent"
              color="#8056DE"
              slider-color="#8056DE"
              align-with-title
              class="no-left-margin"
            >
              <v-tab
                value="metrics"
                @click="handleTabChange('metrics')"
                class="px-2 ml-0 text-none"
              >
                Store Metrics
              </v-tab>
              <v-tab
                value="performance"
                @click="handleTabChange('performance')"
                class="text-none"
              >
                Store Performance
              </v-tab>
              <v-tab
                value="insights"
                @click="handleTabChange('insights')"
                class="text-none"
              >
                Key Insights
              </v-tab>
            </v-tabs>
          </div>

          <!-- New Insights Tab Content -->
          <div v-if="activeTab === 'insights'" class="px-4 pb-8">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Peak Performance Times -->
              <div class="bg-white p-6 rounded-lg shadow-md">
                <h3 class="text-lg font-semibold mb-4">
                  Peak Performance Times
                </h3>
                <div v-if="chartData && chartData.length">
                  <div class="space-y-4">
                    <div class="flex items-center justify-between">
                      <span class="text-gray-600">Best Performing Day</span>
                      <span class="font-medium">{{
                        getBestPerformingDay()
                      }}</span>
                    </div>
                    <!-- Peak Search Time Removed -->
                  </div>
                </div>
              </div>

              <!-- Performance Trends -->
              <div class="bg-white p-6 rounded-lg shadow-md">
                <h3 class="text-lg font-semibold mb-4">Performance Trends</h3>
                <div v-if="chartData && chartData.length">
                  <div class="space-y-4">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-2">
                        <span class="text-gray-600">Search Growth</span>
                        <div class="relative group inline-block">
                          <v-icon small color="gray"
                            >mdi-information-outline</v-icon
                          >
                          <div
                            class="absolute z-50 hidden group-hover:block bg-gray-700 text-white text-xs rounded px-3 py-2 w-[400px] -top-12 left-1/2 transform -translate-x-1/2 shadow-lg"
                          >
                            Percentage change in search impressions comparing
                            the first week to the last week of the selected
                            period.
                          </div>
                        </div>
                      </div>
                      <div
                        class="flex items-center gap-2 min-w-[80px] justify-end"
                      >
                        <span :class="getGrowthClass(getSearchGrowth())">
                          {{ getSearchGrowth() > 0 ? "+" : ""
                          }}{{ getSearchGrowth() }}%
                        </span>
                        <v-icon
                          small
                          :color="getGrowthColor(getSearchGrowth())"
                        >
                          {{ getGrowthIcon(getSearchGrowth()) }}
                        </v-icon>
                      </div>
                    </div>
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-2">
                        <span class="text-gray-600">Action Growth</span>
                        <div class="relative group inline-block">
                          <v-icon small color="gray"
                            >mdi-information-outline</v-icon
                          >
                          <div
                            class="absolute z-50 hidden group-hover:block bg-gray-700 text-white text-xs rounded px-3 py-2 w-[400px] -top-12 left-1/2 transform -translate-x-1/2 shadow-lg"
                          >
                            Percentage change in customer actions (calls,
                            website visits, directions, bookings) comparing the
                            first week to the last week.
                          </div>
                        </div>
                      </div>
                      <div
                        class="flex items-center gap-2 min-w-[80px] justify-end"
                      >
                        <span :class="getGrowthClass(getActionGrowth())">
                          {{ getActionGrowth() > 0 ? "+" : ""
                          }}{{ getActionGrowth() }}%
                        </span>
                        <v-icon
                          small
                          :color="getGrowthColor(getActionGrowth())"
                        >
                          {{ getGrowthIcon(getActionGrowth()) }}
                        </v-icon>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Location Performance -->
              <div class="bg-white p-6 rounded-lg shadow-md">
                <h3 class="text-lg font-semibold mb-4">Location Performance</h3>
                <div v-if="metricsData && metricsData.length">
                  <div class="space-y-4">
                    <div class="flex items-center justify-between">
                      <span class="text-gray-600">Total Locations</span>
                      <span class="font-medium">{{ metricsData.length }}</span>
                    </div>
                    <div class="flex items-center justify-between">
                      <span class="text-gray-600">Most Improved Location</span>
                      <span class="font-medium">{{
                        getMostImprovedLocation()
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Customer Behavior -->
              <div class="bg-white p-6 rounded-lg shadow-md">
                <h3 class="text-lg font-semibold mb-4">Customer Behavior</h3>
                <div v-if="aggregatedData">
                  <div class="space-y-4">
                    <div class="flex items-center justify-between">
                      <span class="text-gray-600">Most Common Action</span>
                      <span class="font-medium">{{
                        getMostCommonAction()
                      }}</span>
                    </div>
                    <div class="flex items-center justify-between">
                      <span class="text-gray-600">Mobile vs Desktop</span>
                      <span class="font-medium">{{
                        getMobileVsDesktopRatio()
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Top 5 Performing Locations -->
              <div class="bg-white p-6 rounded-lg shadow-md mt-6">
                <h3 class="text-lg font-semibold mb-4">
                  Top 5 Performing Locations
                </h3>

                <div v-if="metricsData && metricsData.length">
                  <div class="space-y-3">
                    <div
                      v-for="(location, index) in getTopPerformingLocations()"
                      :key="index"
                      class="flex items-center justify-between py-1"
                    >
                      <span class="font-medium text-sm pr-4 flex-1">{{
                        location.name
                      }}</span>
                      <span class="text-gray-500 text-sm whitespace-nowrap"
                        >{{ location.score.toLocaleString() }} impressions</span
                      >
                    </div>
                    <div
                      v-if="getTopPerformingLocations().length === 0"
                      class="text-gray-500 text-sm"
                    >
                      No data available
                    </div>
                  </div>
                </div>
              </div>

              <!-- Bottom 5 Performing Locations -->
              <div class="bg-white p-6 rounded-lg shadow-md mt-6">
                <h3 class="text-lg font-semibold mb-4">
                  Bottom 5 Performing Locations
                </h3>
                <div v-if="metricsData && metricsData.length">
                  <div class="space-y-3">
                    <div
                      v-for="(
                        location, index
                      ) in getBottomPerformingLocations()"
                      :key="index"
                      class="flex items-center justify-between py-1"
                    >
                      <span class="font-medium text-sm pr-4 flex-1">{{
                        location.name
                      }}</span>
                      <span class="text-gray-500 text-sm whitespace-nowrap"
                        >{{ location.score.toLocaleString() }} impressions</span
                      >
                    </div>
                    <div
                      v-if="getBottomPerformingLocations().length === 0"
                      class="text-gray-500 text-sm"
                    >
                      No data available
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="activeTab === 'performance'"
            class="flex flex-col items-center justify-center w-full pb-20"
          >
            <section
              class="flex flex-col items-center w-full justify-center xl:w-[100%]"
            >
              <div
                class="w-full xl:w-[100%] flex justify-center items-center gap-4"
              >
                <div class="w-full bg-white">
                  <div
                    class="flex flex-col justify-between w-full px-5 mx-auto"
                  >
                    <div v-if="isLoading || showLoadingMessage">
                      <v-skeleton-loader
                        type="table"
                        class="mb-4"
                        boilerplate
                      ></v-skeleton-loader>
                    </div>
                    <div
                      v-else-if="
                        !isLoading &&
                        !showLoadingMessage &&
                        selectedWIDs?.length === 0
                      "
                      class="flex justify-center items-center py-8"
                    >
                      <div
                        class="p-8 bg-white rounded-lg shadow-sm max-w-3xl mx-auto text-center"
                      >
                        <NoLocationsConnected
                          message="Connect your Google Business locations with Fly to start tracking performance metrics. Once connected, you'll see detailed statistics about searches, views, and customer actions."
                          actionText="Connect Google Locations"
                          @click.native="handleConnectGMB"
                        />
                      </div>
                    </div>
                    <div
                      v-else-if="!metricsData || metricsData?.length === 0"
                      class="flex justify-center items-center py-8"
                    >
                      <div
                        class="p-8 bg-white rounded-lg shadow-sm max-w-3xl mx-auto text-center"
                      >
                        <div
                          class="flex flex-col items-center justify-center gap-4"
                        >
                          <v-progress-circular
                            indeterminate
                            color="primary"
                            :size="50"
                            :width="5"
                          ></v-progress-circular>
                          <p class="text-lg font-medium text-gray-700">
                            Please wait while we load your location
                            statistics...
                          </p>
                          <p class="text-sm text-gray-500">
                            This may take a moment to retrieve the latest data.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      v-else
                      class="p-4 bg-white shadow-lg rounded-lg max-w-[1900px]"
                    >
                      <div class="overflow-x-auto w-full">
                        <div class="overflow-auto max-h-[700px]">
                          <div
                            class="bg-gray-100 min-w-[1000px] rounded-t-lg text-xs w-full sticky top-0 z-40"
                          >
                            <div
                              class="flex items-center justify-between text-gray-600 p-2 flex-wrap"
                            >
                              <div
                                class="w-1/6 text-start whitespace-nowrap px-2 cursor-pointer hover:bg-gray-200"
                                @click="sortData('location')"
                              >
                                Location
                                <v-icon small>
                                  {{
                                    sortBy === "location"
                                      ? sortDesc
                                        ? "mdi-arrow-down"
                                        : "mdi-arrow-up"
                                      : "mdi-arrow-up"
                                  }}
                                </v-icon>
                              </div>
                              <div
                                class="w-1/12 text-center whitespace-nowrap px-2 cursor-pointer hover:bg-gray-200"
                                @click="sortData('desktop_maps_impressions')"
                              >
                                Desktop<br />
                                Maps
                                <tool-tip
                                  :message="
                                    getTooltipMessage('maps', 'desktop')
                                  "
                                >
                                  <v-icon small>mdi-information</v-icon>
                                </tool-tip>
                                <v-icon small>
                                  {{
                                    sortBy === "desktop_maps_impressions"
                                      ? sortDesc
                                        ? "mdi-arrow-down"
                                        : "mdi-arrow-up"
                                      : "mdi-arrow-up"
                                  }}
                                </v-icon>
                              </div>
                              <div
                                class="w-1/12 text-center whitespace-nowrap px-2 cursor-pointer hover:bg-gray-200"
                                @click="sortData('mobile_maps_impressions')"
                              >
                                Mobile<br />
                                Maps

                                <tool-tip
                                  :message="getTooltipMessage('maps', 'mobile')"
                                >
                                  <v-icon small>mdi-information</v-icon>
                                </tool-tip>
                                <v-icon small>
                                  {{
                                    sortBy === "mobile_maps_impressions"
                                      ? sortDesc
                                        ? "mdi-arrow-down"
                                        : "mdi-arrow-up"
                                      : "mdi-arrow-up"
                                  }}
                                </v-icon>
                              </div>
                              <div
                                class="w-1/12 text-center whitespace-nowrap px-2"
                                @click="sortData('desktop_search_impressions')"
                              >
                                Desktop<br />
                                Search
                                <tool-tip
                                  :message="
                                    getTooltipMessage('search', 'desktop')
                                  "
                                >
                                  <v-icon small>mdi-information</v-icon>
                                </tool-tip>
                                <v-icon small>
                                  {{
                                    sortBy === "desktop_search_impressions"
                                      ? sortDesc
                                        ? "mdi-arrow-down"
                                        : "mdi-arrow-up"
                                      : "mdi-arrow-up"
                                  }}
                                </v-icon>
                              </div>
                              <div
                                class="w-1/12 text-center whitespace-nowrap px-2"
                                @click="sortData('mobile_search_impressions')"
                              >
                                Mobile<br />
                                Search
                                <tool-tip
                                  :message="
                                    getTooltipMessage('search', 'mobile')
                                  "
                                >
                                  <v-icon small>mdi-information</v-icon>
                                </tool-tip>
                                <v-icon small>
                                  {{
                                    sortBy === "mobile_search_impressions"
                                      ? sortDesc
                                        ? "mdi-arrow-down"
                                        : "mdi-arrow-up"
                                      : "mdi-arrow-up"
                                  }}
                                </v-icon>
                              </div>
                              <div
                                class="w-1/12 text-center whitespace-nowrap px-2 cursor-pointer hover:bg-gray-200"
                                @click="sortData('direction_requests')"
                              >
                                Direction<br />
                                Requests
                                <tool-tip
                                  :message="getTooltipMessage('directions')"
                                >
                                  <v-icon small>mdi-information</v-icon>
                                </tool-tip>
                                <v-icon small>
                                  {{
                                    sortBy === "direction_requests"
                                      ? sortDesc
                                        ? "mdi-arrow-down"
                                        : "mdi-arrow-up"
                                      : "mdi-arrow-up"
                                  }}
                                </v-icon>
                              </div>
                              <div
                                class="w-1/12 text-center whitespace-nowrap px-5 cursor-pointer hover:bg-gray-200"
                                @click="sortData('call_clicks')"
                              >
                                Call<br />
                                Clicks
                                <tool-tip :message="getTooltipMessage('calls')">
                                  <v-icon small>mdi-information</v-icon>
                                </tool-tip>
                                <v-icon small>
                                  {{
                                    sortBy === "call_clicks"
                                      ? sortDesc
                                        ? "mdi-arrow-down"
                                        : "mdi-arrow-up"
                                      : "mdi-arrow-up"
                                  }}
                                </v-icon>
                              </div>
                              <div
                                class="w-1/12 text-center whitespace-nowrap px-2 cursor-pointer hover:bg-gray-200"
                                @click="sortData('website_clicks')"
                              >
                                Website <br />
                                Clicks
                                <tool-tip
                                  :message="getTooltipMessage('website')"
                                >
                                  <v-icon small>mdi-information</v-icon>
                                </tool-tip>
                                <v-icon small>
                                  {{
                                    sortBy === "website_clicks"
                                      ? sortDesc
                                        ? "mdi-arrow-down"
                                        : "mdi-arrow-up"
                                      : "mdi-arrow-up"
                                  }}
                                </v-icon>
                              </div>
                              <div
                                class="w-1/12 text-center whitespace-nowrap pr-6 cursor-pointer hover:bg-gray-200"
                                @click="sortData('bookings')"
                              >
                                Bookings
                                <tool-tip
                                  :message="getTooltipMessage('bookings')"
                                >
                                  <v-icon small>mdi-information</v-icon>
                                </tool-tip>
                                <v-icon small>
                                  {{
                                    sortBy === "bookings"
                                      ? sortDesc
                                        ? "mdi-arrow-down"
                                        : "mdi-arrow-up"
                                      : "mdi-arrow-up"
                                  }}
                                </v-icon>
                              </div>
                              <div
                                class="w-1/12 text-center whitespace-nowrap px-2 cursor-pointer hover:bg-gray-200"
                                @click="sortData('food_orders')"
                              >
                                Food <br />Orders
                                <tool-tip
                                  :message="getTooltipMessage('food_orders')"
                                >
                                  <v-icon small>mdi-information</v-icon>
                                </tool-tip>
                                <v-icon small>
                                  {{
                                    sortBy === "food_orders"
                                      ? sortDesc
                                        ? "mdi-arrow-down"
                                        : "mdi-arrow-up"
                                      : "mdi-arrow-up"
                                  }}
                                </v-icon>
                              </div>
                              <div
                                class="w-1/12 text-center whitespace-nowrap px-4 cursor-pointer hover:bg-gray-200"
                                @click="sortData('food_menu_clicks')"
                              >
                                Food Menu <br />Clicks
                                <tool-tip
                                  :message="getTooltipMessage('food_menu')"
                                >
                                  <v-icon small>mdi-information</v-icon>
                                </tool-tip>
                                <v-icon small>
                                  {{
                                    sortBy === "food_menu_clicks"
                                      ? sortDesc
                                        ? "mdi-arrow-down"
                                        : "mdi-arrow-up"
                                      : "mdi-arrow-up"
                                  }}
                                </v-icon>
                              </div>
                            </div>
                          </div>

                          <div class="min-w-[1000px]">
                            <div
                              v-for="metric in metricsData"
                              :key="metric.location.wid"
                              class="border-b border-gray-200"
                            >
                              <div
                                class="flex justify-between items-center w-full text-xs bg-white hover:bg-gray-100 py-2"
                              >
                                <div class="w-1/6 px-2">
                                  <div class="flex flex-col items-start">
                                    <span class="font-semibold">
                                      {{ metric.location.short_address }}
                                    </span>
                                    <!-- <span
                                      v-if="metric.location?.sub_locality"
                                      class="font-semibold"
                                      >{{ metric.location.sub_locality }}</span
                                    >
                                    <span
                                      v-else-if="
                                        !metric.location?.sub_locality &&
                                        metric.location?.storeLocation
                                      "
                                      class="text-[12px]"
                                      >{{ metric.location.storeLocation }}</span
                                    >
                                    <div
                                      class="flex gap-1 flex-col items-start"
                                    >
                                      <div
                                        v-if="metric.location?.city"
                                        class="text-xs"
                                      >
                                        {{ metric.location.city }}
                                      </div>
                                      <span
                                        v-if="metric.location?.state"
                                        class="text-xs"
                                        >{{ metric.location?.state }}</span
                                      >
                                    </div> -->
                                  </div>
                                </div>
                                <div
                                  class="w-1/12 text-center font-semibold px-2"
                                >
                                  {{ metric.desktop_maps_impressions }}
                                  <div
                                    v-if="
                                      previousMetrics[metric.location.wid] &&
                                      shouldShowPreviousMonthData
                                    "
                                    :class="
                                      getPercentageClass(
                                        metric.desktop_maps_impressions,
                                        previousMetrics[metric.location.wid]
                                          .desktop_maps_impressions
                                      )
                                    "
                                    class="text-[10px] flex items-center justify-center gap-1"
                                  >
                                    <v-icon small>{{
                                      getPercentageIcon(
                                        metric.desktop_maps_impressions,
                                        previousMetrics[metric.location.wid]
                                          .desktop_maps_impressions
                                      )
                                    }}</v-icon>
                                    {{
                                      calculatePercentageChange(
                                        metric.desktop_maps_impressions,
                                        previousMetrics[metric.location.wid]
                                          .desktop_maps_impressions
                                      )
                                    }}%
                                  </div>
                                </div>
                                <div
                                  class="w-1/12 text-center font-semibold px-2"
                                >
                                  {{ metric.mobile_maps_impressions }}
                                  <div
                                    v-if="
                                      previousMetrics[metric.location.wid] &&
                                      shouldShowPreviousMonthData
                                    "
                                    :class="
                                      getPercentageClass(
                                        metric.mobile_maps_impressions,
                                        previousMetrics[metric.location.wid]
                                          .mobile_maps_impressions
                                      )
                                    "
                                    class="text-[10px] flex items-center justify-center gap-1"
                                  >
                                    <v-icon small>{{
                                      getPercentageIcon(
                                        metric.mobile_maps_impressions,
                                        previousMetrics[metric.location.wid]
                                          .mobile_maps_impressions
                                      )
                                    }}</v-icon>
                                    {{
                                      calculatePercentageChange(
                                        metric.mobile_maps_impressions,
                                        previousMetrics[metric.location.wid]
                                          .mobile_maps_impressions
                                      )
                                    }}%
                                  </div>
                                </div>
                                <div
                                  class="w-1/12 text-center font-semibold px-2"
                                >
                                  {{ metric.desktop_search_impressions }}
                                  <div
                                    v-if="
                                      previousMetrics[metric.location.wid] &&
                                      shouldShowPreviousMonthData
                                    "
                                    :class="
                                      getPercentageClass(
                                        metric.desktop_search_impressions,
                                        previousMetrics[metric.location.wid]
                                          .desktop_search_impressions
                                      )
                                    "
                                    class="text-[10px] flex items-center justify-center gap-1"
                                  >
                                    <v-icon small>{{
                                      getPercentageIcon(
                                        metric.desktop_search_impressions,
                                        previousMetrics[metric.location.wid]
                                          .desktop_search_impressions
                                      )
                                    }}</v-icon>
                                    {{
                                      calculatePercentageChange(
                                        metric.desktop_search_impressions,
                                        previousMetrics[metric.location.wid]
                                          .desktop_search_impressions
                                      )
                                    }}%
                                  </div>
                                </div>
                                <div
                                  class="w-1/12 text-center font-semibold px-2"
                                >
                                  {{ metric.mobile_search_impressions }}
                                  <div
                                    v-if="
                                      previousMetrics[metric.location.wid] &&
                                      shouldShowPreviousMonthData
                                    "
                                    :class="
                                      getPercentageClass(
                                        metric.mobile_search_impressions,
                                        previousMetrics[metric.location.wid]
                                          .mobile_search_impressions
                                      )
                                    "
                                    class="text-[10px] flex items-center justify-center gap-1"
                                  >
                                    <v-icon small>{{
                                      getPercentageIcon(
                                        metric.mobile_search_impressions,
                                        previousMetrics[metric.location.wid]
                                          .mobile_search_impressions
                                      )
                                    }}</v-icon>
                                    {{
                                      calculatePercentageChange(
                                        metric.mobile_search_impressions,
                                        previousMetrics[metric.location.wid]
                                          .mobile_search_impressions
                                      )
                                    }}%
                                  </div>
                                </div>
                                <div
                                  class="w-1/12 text-center font-semibold px-2"
                                >
                                  {{ metric.direction_requests }}
                                  <div
                                    v-if="
                                      previousMetrics[metric.location.wid] &&
                                      shouldShowPreviousMonthData
                                    "
                                    :class="
                                      getPercentageClass(
                                        metric.direction_requests,
                                        previousMetrics[metric.location.wid]
                                          .direction_requests
                                      )
                                    "
                                    class="text-[10px] flex items-center justify-center gap-1"
                                  >
                                    <v-icon small>{{
                                      getPercentageIcon(
                                        metric.direction_requests,
                                        previousMetrics[metric.location.wid]
                                          .direction_requests
                                      )
                                    }}</v-icon>
                                    {{
                                      calculatePercentageChange(
                                        metric.direction_requests,
                                        previousMetrics[metric.location.wid]
                                          .direction_requests
                                      )
                                    }}%
                                  </div>
                                </div>
                                <div
                                  class="w-1/12 text-center font-semibold px-2"
                                >
                                  {{ metric.call_clicks }}
                                  <div
                                    v-if="
                                      previousMetrics[metric.location.wid] &&
                                      shouldShowPreviousMonthData
                                    "
                                    :class="
                                      getPercentageClass(
                                        metric.call_clicks,
                                        previousMetrics[metric.location.wid]
                                          .call_clicks
                                      )
                                    "
                                    class="text-[10px] flex items-center justify-center gap-1"
                                  >
                                    <v-icon small>{{
                                      getPercentageIcon(
                                        metric.call_clicks,
                                        previousMetrics[metric.location.wid]
                                          .call_clicks
                                      )
                                    }}</v-icon>
                                    {{
                                      calculatePercentageChange(
                                        metric.call_clicks,
                                        previousMetrics[metric.location.wid]
                                          .call_clicks
                                      )
                                    }}%
                                  </div>
                                </div>
                                <div
                                  class="w-1/12 text-center font-semibold px-2"
                                >
                                  {{ metric.website_clicks }}
                                  <div
                                    v-if="
                                      previousMetrics[metric.location.wid] &&
                                      shouldShowPreviousMonthData
                                    "
                                    :class="
                                      getPercentageClass(
                                        metric.website_clicks,
                                        previousMetrics[metric.location.wid]
                                          .website_clicks
                                      )
                                    "
                                    class="text-[10px] flex items-center justify-center gap-1"
                                  >
                                    <v-icon small>{{
                                      getPercentageIcon(
                                        metric.website_clicks,
                                        previousMetrics[metric.location.wid]
                                          .website_clicks
                                      )
                                    }}</v-icon>
                                    {{
                                      calculatePercentageChange(
                                        metric.website_clicks,
                                        previousMetrics[metric.location.wid]
                                          .website_clicks
                                      )
                                    }}%
                                  </div>
                                </div>
                                <div
                                  class="w-1/12 text-center font-semibold px-2"
                                >
                                  {{ metric.bookings }}
                                  <div
                                    v-if="
                                      previousMetrics[metric.location.wid] &&
                                      shouldShowPreviousMonthData
                                    "
                                    :class="
                                      getPercentageClass(
                                        metric.bookings,
                                        previousMetrics[metric.location.wid]
                                          .bookings
                                      )
                                    "
                                    class="text-[10px] flex items-center justify-center gap-1"
                                  >
                                    <v-icon small>{{
                                      getPercentageIcon(
                                        metric.bookings,
                                        previousMetrics[metric.location.wid]
                                          .bookings
                                      )
                                    }}</v-icon>
                                    {{
                                      calculatePercentageChange(
                                        metric.bookings,
                                        previousMetrics[metric.location.wid]
                                          .bookings
                                      )
                                    }}%
                                  </div>
                                </div>
                                <div
                                  class="w-1/12 text-center font-semibold px-2"
                                >
                                  {{ metric.food_orders }}
                                  <div
                                    v-if="
                                      previousMetrics[metric.location.wid] &&
                                      shouldShowPreviousMonthData
                                    "
                                    :class="
                                      getPercentageClass(
                                        metric.food_orders,
                                        previousMetrics[metric.location.wid]
                                          .food_orders
                                      )
                                    "
                                    class="text-[10px] flex items-center justify-center gap-1"
                                  >
                                    <v-icon small>{{
                                      getPercentageIcon(
                                        metric.food_orders,
                                        previousMetrics[metric.location.wid]
                                          .food_orders
                                      )
                                    }}</v-icon>
                                    {{
                                      calculatePercentageChange(
                                        metric.food_orders,
                                        previousMetrics[metric.location.wid]
                                          .food_orders
                                      )
                                    }}%
                                  </div>
                                </div>
                                <div
                                  class="w-1/12 text-center font-semibold px-2"
                                >
                                  {{ metric.food_menu_clicks }}
                                  <div
                                    v-if="
                                      previousMetrics[metric.location.wid] &&
                                      shouldShowPreviousMonthData
                                    "
                                    :class="
                                      getPercentageClass(
                                        metric.food_menu_clicks,
                                        previousMetrics[metric.location.wid]
                                          .food_menu_clicks
                                      )
                                    "
                                    class="text-[10px] flex items-center justify-center gap-1"
                                  >
                                    <v-icon small>{{
                                      getPercentageIcon(
                                        metric.food_menu_clicks,
                                        previousMetrics[metric.location.wid]
                                          .food_menu_clicks
                                      )
                                    }}</v-icon>
                                    {{
                                      calculatePercentageChange(
                                        metric.food_menu_clicks,
                                        previousMetrics[metric.location.wid]
                                          .food_menu_clicks
                                      )
                                    }}%
                                  </div>
                                </div>
                              </div>
                              <div
                                v-if="
                                  selectedDate?.months &&
                                  shouldShowPreviousMonthData
                                "
                                class="flex items-center justify-start w-full px-2 py-1"
                              >
                                <button
                                  @click="
                                    togglePreviousData(metric.location.wid)
                                  "
                                  class="text-xs text-gray-600 hover:text-gray-800 flex items-center gap-1"
                                >
                                  <v-icon small>{{
                                    showPreviousData[metric.location.wid]
                                      ? "mdi-chevron-up"
                                      : "mdi-chevron-down"
                                  }}</v-icon>
                                </button>
                              </div>
                              <div
                                v-if="
                                  showPreviousData[metric.location.wid] &&
                                  shouldShowPreviousMonthData
                                "
                                class="bg-gray-50 px-2 py-3 border-t border-gray-200"
                              >
                                <div
                                  v-if="!previousMetrics[metric.location.wid]"
                                  class="flex justify-between items-center w-full text-xs"
                                >
                                  <div class="w-1/6 px-2">
                                    <div class="skeleton-loader w-24 h-4"></div>
                                  </div>
                                  <div class="w-1/12 text-center">
                                    <div
                                      class="skeleton-loader w-12 h-4 mx-auto"
                                    ></div>
                                  </div>
                                  <div class="w-1/12 text-center">
                                    <div
                                      class="skeleton-loader w-12 h-4 mx-auto"
                                    ></div>
                                  </div>
                                  <div class="w-1/12 text-center">
                                    <div
                                      class="skeleton-loader w-12 h-4 mx-auto"
                                    ></div>
                                  </div>
                                  <div class="w-1/12 text-center">
                                    <div
                                      class="skeleton-loader w-12 h-4 mx-auto"
                                    ></div>
                                  </div>
                                  <div class="w-1/12 text-center">
                                    <div
                                      class="skeleton-loader w-12 h-4 mx-auto"
                                    ></div>
                                  </div>
                                  <div class="w-1/12 text-center">
                                    <div
                                      class="skeleton-loader w-12 h-4 mx-auto"
                                    ></div>
                                  </div>
                                  <div class="w-1/12 text-center">
                                    <div
                                      class="skeleton-loader w-12 h-4 mx-auto"
                                    ></div>
                                  </div>
                                  <div class="w-1/12 text-center">
                                    <div
                                      class="skeleton-loader w-12 h-4 mx-auto"
                                    ></div>
                                  </div>
                                  <div class="w-1/12 text-center">
                                    <div
                                      class="skeleton-loader w-12 h-4 mx-auto"
                                    ></div>
                                  </div>
                                </div>
                                <div
                                  v-else
                                  class="flex justify-between items-center w-full text-xs"
                                >
                                  <!-- Your existing previous metrics display -->
                                  <div class="w-1/6 px-2">
                                    <span class="text-gray-600"
                                      >Previous
                                      {{ selectedDate.months }} Months</span
                                    >
                                  </div>
                                  <div class="w-1/12 text-center">
                                    {{
                                      previousMetrics[metric.location.wid]
                                        .desktop_maps_impressions
                                    }}
                                  </div>
                                  <div class="w-1/12 text-center">
                                    {{
                                      previousMetrics[metric.location.wid]
                                        .mobile_maps_impressions
                                    }}
                                  </div>
                                  <div class="w-1/12 text-center">
                                    {{
                                      previousMetrics[metric.location.wid]
                                        .desktop_search_impressions
                                    }}
                                  </div>
                                  <div class="w-1/12 text-center">
                                    {{
                                      previousMetrics[metric.location.wid]
                                        .mobile_search_impressions
                                    }}
                                  </div>
                                  <div class="w-1/12 text-center">
                                    {{
                                      previousMetrics[metric.location.wid]
                                        .direction_requests
                                    }}
                                  </div>
                                  <div class="w-1/12 text-center">
                                    {{
                                      previousMetrics[metric.location.wid]
                                        .call_clicks
                                    }}
                                  </div>
                                  <div class="w-1/12 text-center">
                                    {{
                                      previousMetrics[metric.location.wid]
                                        .website_clicks
                                    }}
                                  </div>
                                  <div class="w-1/12 text-center">
                                    {{
                                      previousMetrics[metric.location.wid]
                                        .bookings
                                    }}
                                  </div>
                                  <div class="w-1/12 text-center">
                                    {{
                                      previousMetrics[metric.location.wid]
                                        .food_orders
                                    }}
                                  </div>
                                  <div class="w-1/12 text-center">
                                    {{
                                      previousMetrics[metric.location.wid]
                                        .food_menu_clicks
                                    }}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <div class="flex justify-center mt-6">
              <div class="flex items-center gap-4">
                <button
                  @click="prevPage('performance')"
                  :disabled="currentPage === 1"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border disabled:cursor-not-allowed border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <div class="flex items-center gap-2">
                  <span class="text-sm text-gray-700">Page</span>
                  <span class="font-medium">{{
                    totalPages ? currentPage : 0
                  }}</span>
                  <span class="text-sm text-gray-700">of</span>
                  <span class="font-medium">{{ totalPages || 0 }}</span>
                </div>
                <button
                  @click="nextPage('performance')"
                  :disabled="currentPage >= totalPages"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
          <div
            v-else-if="activeTab === 'metrics'"
            class="bg-white p-4 pt-0 pl-0 mt-4 w-full mx-2"
          >
            <div
              v-if="!isLoading && !isChartLoading && selectedWIDs?.length === 0"
              class="flex justify-center items-center py-8"
            >
              <div
                class="p-8 bg-white rounded-lg shadow-sm max-w-3xl mx-auto text-center"
              >
                <NoLocationsConnected
                  message="Connect your Google Business locations with Fly to start tracking performance metrics. Once connected, you'll see detailed statistics about searches, views, and customer actions."
                  actionText="Connect Google Locations"
                  @click.native="handleConnectGMB"
                />
              </div>
            </div>
            <div
              v-else-if="
                selectedWIDs?.length > 0 &&
                (!chartData || chartData?.length === 0)
              "
              class="flex justify-center items-center py-8"
            >
              <div
                class="p-8 bg-white rounded-lg shadow-sm max-w-3xl mx-auto text-center"
              >
                <div class="flex flex-col items-center justify-center gap-4">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    :size="50"
                    :width="5"
                  ></v-progress-circular>
                  <p class="text-lg font-medium text-gray-700">
                    Loading location metrics...
                  </p>
                  <p class="text-sm text-gray-500">
                    Your charts will appear shortly. Thank you for your
                    patience.
                  </p>
                </div>
              </div>
            </div>
            <div v-show="!isChartLoading && chartData?.length">
              <!-- Graph for mobile metrics -->
              <div>
                <div class="flex items-start gap-4">
                  <div v-for="metric in mobileMetricsModel" :key="metric.value">
                    <input
                      type="checkbox"
                      :id="metric.value"
                      v-model="selectedMobileMetricsModel"
                      :value="metric.value"
                      @change="
                        saveMetricsPreference(
                          'mobile',
                          selectedMobileMetricsModel
                        )
                      "
                      :style="{
                        accentColor: selectedMobileMetricsModel.includes(
                          metric.value
                        )
                          ? metricColors[metric.value]
                          : '',
                      }"
                    />
                    <label
                      :for="metric.value"
                      class="text-sm pl-2 font-semibold"
                      :style="{ color: metricColors[metric.value] }"
                      >{{ metric.text }}</label
                    >
                  </div>
                </div>

                <div class="relative h-[400px] w-full gap-9">
                  <canvas ref="mobileMetricsChart"></canvas>
                </div>
              </div>
              <!-- Graph for desktp metrics -->
              <div>
                <div class="flex items-start gap-4">
                  <div
                    v-for="metric in desktopMetricsModel"
                    :key="metric.value"
                  >
                    <input
                      type="checkbox"
                      :id="metric.value"
                      v-model="selectedDesktopMetricsModel"
                      :value="metric.value"
                      @change="
                        saveMetricsPreference(
                          'desktop',
                          selectedDesktopMetricsModel
                        )
                      "
                      :style="{
                        accentColor: selectedDesktopMetricsModel.includes(
                          metric.value
                        )
                          ? metricColors[metric.value]
                          : '',
                      }"
                    />
                    <label
                      :for="metric.value"
                      class="text-sm pl-2 font-semibold"
                      :style="{ color: metricColors[metric.value] }"
                      >{{ metric.text }}</label
                    >
                  </div>
                </div>
                <div class="relative h-[400px] w-full gap-9">
                  <canvas ref="desktopMetricsChart"></canvas>
                </div>
              </div>
              <!-- Graph for rest of the metrics -->
              <div>
                <div class="flex items-start gap-4">
                  <div v-for="metric in availableMetrics" :key="metric.value">
                    <input
                      type="checkbox"
                      :id="metric.value"
                      v-model="selectedMetricsModel"
                      :value="metric.value"
                      @change="
                        saveMetricsPreference('default', selectedMetricsModel)
                      "
                      :style="{
                        accentColor: selectedMetricsModel.includes(metric.value)
                          ? metricColors[metric.value]
                          : '',
                      }"
                    />
                    <label
                      :for="metric.value"
                      class="text-sm pl-2 font-semibold"
                      :style="{ color: metricColors[metric.value] }"
                      >{{ metric.text }}</label
                    >
                  </div>
                </div>
                <div class="relative h-[400px] w-full gap-9">
                  <canvas ref="metricsChart"></canvas>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  collection,
  query,
  where,
  getDoc,
  doc,
  getDocs,
} from "firebase/firestore";
import { auth, db } from "@/utils/firebase.utils";
import ToolTip from "@/components/ui/ToolTip.vue";
import AggregatedMetricUi from "@/components/AggregatedMetricUi.vue";
import Chart from "chart.js/auto";
import LocationDropdown from "@/components/LocationDropdown.vue";
import connectedAppsMixins from "@/mixins/connected.apps.mixins";
import NoLocationsConnected from "@/components/ui/common/NoLocationsConnected.vue";
import html2canvas from "html2canvas";

export default {
  mixins: [connectedAppsMixins],
  components: {
    ToolTip,
    LocationDropdown,
    AggregatedMetricUi,
    NoLocationsConnected,
  },
  props: {},
  data() {
    return {
      stores: [],
      lastFetchedDate: null,
      partialData: {},
      tabIndex: 0,
      pendingWorkspaces: [],
      showStoreDropdown: false,
      selectedWIDs: [],
      metricsData: {},
      isLoading: false,
      isChartLoading: false,
      isAggregatedDataLoading: false,
      selectedDateRange: "Last 7 days",
      sortBy: "location",
      sortDesc: false,
      locationTitles: {},
      gmb_logo: "",
      dateOptions: [],
      selectedDate: null,
      isLoadingDates: false,
      // Custom date range properties
      showCustomDateRange: false,
      customStartDate: null,
      customEndDate: null,
      isCustomDateRange: false,
      isCustomDataFetching: false, // Flag to prevent multiple simultaneous API calls
      gmbStatsStartDate: null, // GMB stats start date from admin doc
      metricsChart: null,
      mobileMetricsChart: null,
      desktopMetricsChart: null,
      chartData: [],
      selectedMetricsModel: [],
      selectedMobileMetricsModel: [],
      selectedDesktopMetricsModel: [],
      mobileMetricsModel: [
        { text: "Mobile Maps", value: "mobile_maps_impressions" },
        { text: "Mobile Search", value: "mobile_search_impressions" },
      ],
      desktopMetricsModel: [
        { text: "Desktop Maps", value: "desktop_maps_impressions" },
        { text: "Desktop Search", value: "desktop_search_impressions" },
      ],
      availableMetrics: [
        { text: "Direction Requests", value: "direction_requests" },
        { text: "Call Clicks", value: "call_clicks" },
        { text: "Website Clicks", value: "website_clicks" },
        { text: "Bookings", value: "bookings" },
        { text: "Food Orders", value: "food_orders" },
        { text: "Food Menu Clicks", value: "food_menu_clicks" },
      ],
      activeTab: "metrics",
      mobileMetricColors: {
        mobile_maps_impressions: "#48BB78",
        mobile_search_impressions: "#ED8936",
      },
      desktopMetricColors: {
        desktop_maps_impressions: "#4C51BF",
        desktop_search_impressions: "#FF6B6B",
      },
      metricColors: {
        desktop_maps_impressions: "#4C51BF",
        mobile_maps_impressions: "#48BB78",
        desktop_search_impressions: "#FF6B6B",
        mobile_search_impressions: "#ED8936",
        direction_requests: "#A888B5",
        call_clicks: "#38B2AC",
        website_clicks: "#DA498D",
        bookings: "#4299E1",
        food_orders: "#FF8383",
        food_menu_clicks: "#FCC737",
      },
      locationSearch: "",
      currentPage: 1,
      itemsPerPage: 10,
      paginatedWIDs: [],
      paginatedGraphWIDs: [],
      showPreviousData: {},
      previousMetrics: {},
      showLoadingMessage: false,
      allFetchedData: {},
      locationTitlesMapping: {},
      aggregatedData: {},
      dataFetched: false,
      previousAggregatedData: {},
      previousMonthDataFetched: false,
      previousMonthAggregatedDataLoading: false,
      metricsFetchedData: {},
      isStoreDataAvailable: true,
      rawAggregatedMetrics: [], // Store the raw backend response
      previousRawAggregatedMetrics: [], // Store the raw backend response
      performanceReportLoader: false,
      showDownloadMenu: false,
      downloadUrls: {
        excel_url: null,
        csv_url: null,
      },
      isGeneratingReport: false,
      showScreenshotHeader: false,
      workspaceData: null,
      dateValidationError: null,
    };
  },
  computed: {
    currentWorkspace() {
      return this.$store.getters.currentWorkspace;
    },
    currentWorkspaceName() {
      return (
        this.workspaceData?.name || this.currentWorkspace?.name || "Workspace"
      );
    },
    currentWorkspaceLogo() {
      return (
        this.workspaceData?.logo_original ||
        this.currentWorkspace?.logo_original
      );
    },
    currentDate() {
      const now = new Date();
      return now.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
      });
    },
    isCurrentMonth() {
      const currentDate = new Date();
      return (
        this.selectedDate?.year === currentDate.getFullYear() &&
        this.selectedDate?.month === currentDate.getMonth() + 1
      );
    },
    totalPages() {
      return Math.ceil(this.selectedWIDs.length / this.itemsPerPage) || 0;
    },
    filteredStores() {
      if (!this.locationSearch) {
        return this.stores;
      }

      const searchTerm = this.locationSearch.toLowerCase();
      return this.stores.filter(
        (store) =>
          store.name?.toLowerCase().includes(searchTerm) ||
          store.sub_locality?.toLowerCase().includes(searchTerm) ||
          store.city?.toLowerCase().includes(searchTerm) ||
          store.state?.toLowerCase().includes(searchTerm)
      );
    },
    // Date validation for custom date range
    maxStartDate() {
      const today = new Date();
      const maxDate = new Date(today);
      maxDate.setDate(today.getDate() - 7); // Block last 7 days
      return maxDate.toISOString().split("T")[0];
    },
    maxEndDate() {
      const today = new Date();
      const maxDate = new Date(today);
      maxDate.setDate(today.getDate() - 7); // Block last 7 days
      return maxDate.toISOString().split("T")[0];
    },

    // Minimum allowed date based on GMB stats start date
    minStartDate() {
      if (this.gmbStatsStartDate) {
        // Convert start_date string (e.g., "2023-04-01") to ISO format
        return this.gmbStatsStartDate;
      }
      // If no GMB start date is available, allow any date
      return null;
    },

    // Minimum allowed date for end date picker (based on selected start date or GMB start date)
    minEndDate() {
      if (this.customStartDate) {
        return this.customStartDate;
      }
      return this.minStartDate;
    },

    // Formatted display for start date text field
    formattedStartDate() {
      if (this.customStartDate) {
        return new Date(this.customStartDate).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
      return "";
    },

    // Formatted display for end date text field
    formattedEndDate() {
      if (this.customEndDate) {
        return new Date(this.customEndDate).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
      return "";
    },

    // Computed property to determine if previous month data should be shown
    shouldShowPreviousMonthData() {
      if (!this.selectedDate) return false;

      // For custom date ranges: don't show previous data if > 6 months (180 days)
      if (this.selectedDate.isCustom) {
        return this.selectedDate.totalDays <= 180;
      }

      // For preset months: don't show previous data for 12 months
      return this.selectedDate.months !== 12;
    },

    canApplyCustomDateRange() {
      return (
        this.customStartDate &&
        this.customEndDate &&
        !this.dateValidationError &&
        !this.isLoadingDates &&
        !this.isChartLoading &&
        !this.isAggregatedDataLoading
      );
    },

    isThisMonthDisabled() {
      const today = new Date();
      const dayOfMonth = today.getDate();
      // Disable if today's date is 7 or less (not enough data available)
      return dayOfMonth <= 7;
    },
  },
  watch: {
    // Watch for WID changes to refetch GMB stats start date
    WID: {
      handler(newWID) {
        if (newWID) {
          this.fetchGmbStatsStartDate();
        }
      },
      immediate: false,
    },
    selectedWIDs(newVal) {
      if (!newVal.length) {
        this.currentPage = 1;
        this.updatePaginatedWIDs();
        this.chartData = [];
        this.destroyChart("metricsChart");
        this.destroyChart("mobileMetricsChart");
        this.destroyChart("desktopMetricsChart");
      }
    },
    // update locations on change in query
    "$route.query.location": {
      handler(newVal) {
        if (newVal) {
          this.selectedWIDs = newVal.split(",");
          this.stores.forEach((store) => {
            if (!store.shallowConnect) {
              store.checked = this.selectedWIDs.includes(store.wid);
            }
          });
        }
      },
    },
    activeTab(newVal) {
      if (newVal === "metrics") {
        // Ensure chart is rendered when switching to metrics tab
        this.$nextTick(() => {
          this.updateAllCharts();
          this.$forceUpdate();
        });
      }
    },
    "selectedDate.months": {
      async handler() {
        // Reset previous data state
        this.showPreviousData = {};
        this.previousMetrics = {};
        this.metricsData = [];
        this.chartData = [];
        // Ensure reactivity
        this.$set(this, "chartData", []);
        this.$set(this, "metricsData", []);
        // Reopen all dropdowns and fetch new data
        this.selectedWIDs.forEach((wid) => {
          this.$set(this.showPreviousData, wid, true);
        });
        if (!this.selectedDate) return;
        await Promise.all([
          this.aggregatedMetrics("current"),
          this.aggregatedMetrics("previous"),
        ]);
        await this.refreshData();
      },
      immediate: true,
    },
  },
  async created() {
    this.tabIndex = this.activeTab === "metrics" ? 0 : 1;
    this.generateDateOptions();
    this.selectedMetricsModel = this.selectedMetrics;
    this.selectedMobileMetricsModel = this.selectedMobileMetrics;
    this.selectedDesktopMetricsModel = this.selectedDesktopMetrics;
    if (this.selectedStatsMonth) {
      const date = this.dateOptions.find(
        (option) => option.value.months === this.selectedStatsMonth
      );
      this.selectedDate = date.value;
    }
    if (this.selectedStatsLocations?.length) {
      this.selectedWIDs = this.selectedStatsLocations;
    }
    // console.log('created data :',this.selectedStatsMonth, this.selectedStatsLocations, this.allFetchedLocationStatsData);
  },
  methods: {
    async fetchWorkspaceData() {
      if (!this.WID || !this.currentUID) {
        console.warn("Cannot fetch workspace data: missing WID or currentUID");
        return;
      }

      try {
        const workspaceRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID
        );
        const workspaceDoc = await getDoc(workspaceRef);

        if (workspaceDoc.exists()) {
          this.workspaceData = workspaceDoc.data();
        } else {
          console.warn("Workspace not found in Firestore for WID:", this.WID);
        }
      } catch (error) {
        console.error("Error fetching workspace data:", error);
      }
    },

    async fetchGmbStatsStartDate() {
      if (!this.WID || !this.currentUID) {
        console.warn(
          "Cannot fetch GMB stats start date: missing WID or currentUID"
        );
        return;
      }

      try {
        // Fetch the admin document using this.WID
        const adminDocRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID
        );
        const adminDoc = await getDoc(adminDocRef);

        if (adminDoc.exists()) {
          const adminData = adminDoc.data();

          // Check if gmb_stats object exists and has start_date
          if (adminData.gmb_stats && adminData.gmb_stats.start_date) {
            this.gmbStatsStartDate = adminData.gmb_stats.start_date;
            console.log(
              "GMB stats start date fetched:",
              this.gmbStatsStartDate
            );
          } else {
            console.warn("GMB stats start date not found in admin document");
            this.gmbStatsStartDate = null;
          }
        } else {
          console.warn("Admin document not found for WID:", this.WID);
          this.gmbStatsStartDate = null;
        }
      } catch (error) {
        console.error("Error fetching GMB stats start date:", error);
        this.gmbStatsStartDate = null;
      }
    },
    getImpressions() {
      if (!this.aggregatedData) return 0;
      return (
        Number(this.aggregatedData.total_desktop_maps_impressions || 0) +
        Number(this.aggregatedData.total_mobile_maps_impressions || 0) +
        Number(this.aggregatedData.total_desktop_search_impressions || 0) +
        Number(this.aggregatedData.total_mobile_search_impressions || 0)
      );
    },
    getTotalScore() {
      if (!this.aggregatedData) return 0;

      const impressions =
        Number(this.aggregatedData.total_desktop_maps_impressions || 0) +
        Number(this.aggregatedData.total_mobile_maps_impressions || 0) +
        Number(this.aggregatedData.total_desktop_search_impressions || 0) +
        Number(this.aggregatedData.total_mobile_search_impressions || 0);

      const actions =
        Number(this.aggregatedData.total_direction_requests || 0) +
        Number(this.aggregatedData.total_call_clicks || 0) +
        Number(this.aggregatedData.total_website_clicks || 0) +
        Number(this.aggregatedData.total_bookings || 0) +
        Number(this.aggregatedData.total_food_orders || 0) +
        Number(this.aggregatedData.total_food_menu_clicks || 0);

      const conversionRate =
        impressions > 0 ? (actions / impressions) * 100 : 0;

      const k = 0.00002079;
      const m = 0.0002079;

      const visibilityScore = 40 * (1 - Math.exp(-k * impressions));
      const engagementScore = 40 * (1 - Math.exp(-m * actions));
      const conversionScore = Math.min(20, (conversionRate / 10) * 20);

      const totalScore = visibilityScore + engagementScore + conversionScore;

      return isNaN(totalScore) ? 0 : Math.round(totalScore);
    },

    getScoreBreakdown() {
      if (!this.aggregatedData) return null;

      const visibilityScore =
        (this.aggregatedData.total_desktop_maps_impressions || 0) +
        (this.aggregatedData.total_mobile_maps_impressions || 0) +
        (this.aggregatedData.total_desktop_search_impressions || 0) +
        (this.aggregatedData.total_mobile_search_impressions || 0);

      const engagementScore =
        (this.aggregatedData.total_direction_requests || 0) +
        (this.aggregatedData.total_call_clicks || 0) +
        (this.aggregatedData.total_website_clicks || 0) +
        (this.aggregatedData.total_bookings || 0) +
        (this.aggregatedData.total_food_orders || 0) +
        (this.aggregatedData.total_food_menu_clicks || 0);

      const conversionRate =
        visibilityScore > 0 ? (engagementScore / visibilityScore) * 100 : 0;

      return {
        visibility: {
          raw: visibilityScore,
          normalized: Math.min(50, (visibilityScore / 1000) * 2.5),
          maxScore: 50,
        },
        engagement: {
          raw: engagementScore,
          normalized: Math.min(35, (engagementScore / 100) * 3.5),
          maxScore: 35,
        },
        conversion: {
          raw: conversionRate,
          normalized: Math.min(15, (conversionRate / 5) * 15),
          maxScore: 15,
        },
      };
    },
    async generateReport() {
      try {
        // Add validation
        if (!this.selectedWIDs || this.selectedWIDs.length === 0) {
          this.$snackbar.show({
            message:
              "Please select at least one location to generate the report.",
            color: "error",
          });
          return;
        }

        if (!this.selectedDate) {
          this.$snackbar.show({
            message: "Please select a date range to generate the report.",
            color: "error",
          });
          return;
        }

        // Close any existing menu first
        this.showDownloadMenu = false;
        console.log("showDownloadMenu bb", this.showDownloadMenu);

        this.isGeneratingReport = true;

        // Show loading message
        this.$snackbar.show({
          message: "Generating report, please wait...",
          color: "info",
        });

        const idToken = await auth.currentUser.getIdToken();
        
        let payload;
        
        if (this.selectedDate.isCustom) {
          // Handle custom date range
          const currentStartDate = new Date(this.selectedDate.startDate);
          const currentEndDate = new Date(this.selectedDate.endDate);
          
          // Calculate previous period for custom range (same duration, ending 1 day before current starts)
          const duration = currentEndDate - currentStartDate;
          const previousEndDate = new Date(currentStartDate);
          previousEndDate.setDate(previousEndDate.getDate() - 1);
          const previousStartDate = new Date(previousEndDate - duration);
          
          payload = {
            uid: this.currentUID,
            current: {
              start: this.formatDateForPayload(currentStartDate),
              end: this.formatDateForPayload(currentEndDate),
            },
            previous: {
              start: this.formatDateForPayload(previousStartDate),
              end: this.formatDateForPayload(previousEndDate),
            },
            wids: this.selectedWIDs,
            action: "generate_report",
          };
        } else {
          // Handle preset date ranges (last N months)
          const selectedMonths = this.selectedDate?.months;
          const dateRanges = this.calculateDateRanges(selectedMonths);
          
          payload = {
            uid: this.currentUID,
            current: {
              start: this.formatDateForPayload(dateRanges.current.start),
              end: this.formatDateForPayload(dateRanges.current.end),
            },
            previous: {
              start: this.formatDateForPayload(dateRanges.previous.start),
              end: this.formatDateForPayload(dateRanges.previous.end),
            },
            wids: this.selectedWIDs,
            action: "generate_report",
          };
        }

        const startTime = Date.now();

        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/generateGmbStatsReport`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ data: payload }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const endTime = Date.now();
        const duration = endTime - startTime;
        console.log(`Report generation time: ${duration} milliseconds`);

        const resultData = await response.json();
        console.log("Success! Report generated:", resultData);

        // Check if the response has the expected structure
        if (!resultData || !resultData.status) {
          throw new Error("Invalid response format from server");
        }

        // Store the download URLs
        this.downloadUrls = {
          excel_url: resultData.excel_url || null,
          csv_url: resultData.csv_url || null,
        };

        // Show success message
        this.$snackbar.show({
          message:
            "Report generated successfully! Click the download button to select format.",
          color: "success",
        });

        // Open the download menu after a short delay to ensure the loading state is cleared
        this.$nextTick(() => {
          this.showDownloadMenu = true;
        });
        console.log("showDownloadMenu", this.showDownloadMenu);
      } catch (error) {
        console.error("Error generating report:", error);

        let errorMessage = "Failed to generate report. Please try again.";

        if (error.message.includes("HTTP error")) {
          errorMessage =
            "Server error occurred while generating the report. Please try again later.";
        } else if (error.message.includes("Invalid response format")) {
          errorMessage =
            "Report generation completed but response format is invalid. Please contact support.";
        } else if (error.message.includes("Failed to fetch")) {
          errorMessage =
            "Network error occurred. Please check your connection and try again.";
        }

        this.$snackbar.show({
          message: errorMessage,
          color: "error",
        });
      } finally {
        this.isGeneratingReport = false;
      }
    },

    async downloadFile(fileType) {
      try {
        const downloadUrl =
          fileType === "excel"
            ? this.downloadUrls.excel_url
            : this.downloadUrls.csv_url;

        if (!downloadUrl) {
          this.$snackbar.show({
            message: `${fileType.toUpperCase()} file is not available.`,
            color: "error",
          });
          return;
        }

        // Show downloading message
        this.$snackbar.show({
          message: `Downloading ${fileType.toUpperCase()} file...`,
          color: "info",
        });

        // Fetch the file
        const response = await fetch(downloadUrl, {
          method: "GET",
          headers: {
            Accept:
              fileType === "excel"
                ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                : "text/csv,application/csv,*/*",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Failed to download file: ${response.status} ${response.statusText}`
          );
        }

        // Get the file as blob
        const blob = await response.blob();
        const fileExtension = fileType === "excel" ? "xlsx" : "csv";

        // Create blob URL
        const blobUrl = window.URL.createObjectURL(blob);

        // Create download link
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `gmb-stats-report-${new Date()
          .toISOString()
          .slice(0, 10)}.${fileExtension}`;

        link.style.display = "none";

        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();

        // Clean up
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
          document.body.removeChild(link);
        }, 100);

        // Close the menu
        this.showDownloadMenu = false;

        // Show success message
        this.$snackbar.show({
          message: `${fileType.toUpperCase()} file downloaded successfully!`,
          color: "success",
        });
      } catch (error) {
        console.error(`Error downloading ${fileType} file:`, error);

        this.$snackbar.show({
          message: `Failed to download ${fileType.toUpperCase()} file. Please try again.`,
          color: "error",
        });
      }
    },
    // ⬇️ NEW: take a screenshot of the whole white-card dashboard
    async captureScreenshot() {
      try {
        // optional snackbar if you already use one
        this.$store?.commit("showSnackbar", {
          text: "Preparing screenshot…",
          color: "info",
        });

        // Show header for screenshot
        this.showScreenshotHeader = true;

        // Wait for DOM update
        await this.$nextTick();

        // Grab the main white card – adjust selector if you renamed classes
        const target = this.$el.querySelector(".bg-white.rounded-lg.shadow-sm");
        if (!target) throw new Error("Dashboard element not found");

        const canvas = await html2canvas(target, {
          scale: 2, // 2× pixel density = crisper PNG
          useCORS: true,
          backgroundColor: "#ffffff",
        });
        // Hide header after screenshot
        this.showScreenshotHeader = false;

        // Download it
        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = `${
          this.currentWorkspaceName
        }-location-stats-${new Date().toISOString().slice(0, 10)}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$store?.commit("showSnackbar", {
          text: "Screenshot saved",
          color: "success",
        });
      } catch (err) {
        console.error("Screenshot failed:", err);
        this.$store?.commit("showSnackbar", {
          text: "Failed to save screenshot",
          color: "error",
        });
      }
    },
    needsFreshData(monthSelection, type = "current") {
      const today = new Date().toLocaleDateString("en-GB");
      const storedData =
        type === "current"
          ? this.$store.state._currentAggregatedMetrics[monthSelection]
          : this.$store.state._previousAggregatedMetrics[monthSelection];
      return !storedData || storedData.lastFetchedDate !== today;
    },
    async aggregatedMetrics(type = "current") {
      // Skip this operation if no locations are selected
      if (!this.selectedWIDs || this.selectedWIDs.length === 0) {
        return;
      }
      const nonShallowSelectedWIDs = this.selectedWIDs.filter((wid) => {
        const store = this.stores.find((s) => s.wid === wid);
        return store && !store.shallowConnect;
      });

      // If there are no non-shallow locations, return early
      if (nonShallowSelectedWIDs.length === 0) {
        if (type == "current") {
          this.isAggregatedDataLoading = false;
        } else if (type == "previous") {
          this.previousMonthAggregatedDataLoading = false;
        }
        return;
      }

      if (type == "current") {
        this.isAggregatedDataLoading = true;
      } else if (type == "previous") {
        this.previousMonthAggregatedDataLoading = true;
      }
      // Don't fetch previous data for >6 months (last 1 year and >6 months in custom date range)
      if (type === "previous") {
        if (this.selectedDate && this.selectedDate.months === 12) {
          return; // Skip for last 1 year
        }
        // For custom ranges, this method shouldn't be called, but add safety check
        if (
          this.selectedDate &&
          this.selectedDate.isCustom &&
          this.selectedDate.totalDays > 180
        ) {
          return; // Skip for custom ranges >6 months (180 days)
        }
      }

      // Skip aggregated metrics for custom date ranges - they use different method
      if (this.selectedDate && this.selectedDate.isCustom) {
        return;
      }

      const selectedMonths = this.selectedDate?.months;
      const dateRanges = this.calculateDateRanges(selectedMonths);
      // Check if we have fresh data in store
      if (!this.needsFreshData(selectedMonths, type)) {
        // Return stored data if it's from today
        const storedData =
          type === "current"
            ? this.$store.state._currentAggregatedMetrics[selectedMonths].data
            : this.$store.state._previousAggregatedMetrics[selectedMonths].data;
        this.rawAggregatedMetrics = storedData;
        if (type == "current") {
          this.rawAggregatedMetrics = storedData;
          this.computeAggregatedDataFromRaw("current");
        } else {
          this.previousRawAggregatedMetrics = storedData;
          this.computeAggregatedDataFromRaw("previous");
        }
        return;
      } else {
        try {
          let startDate = new Date();
          let endDate = new Date();
          startDate.setMonth(startDate.getMonth() - selectedMonths);
          const currentDate = new Date();
          const currentDay = startDate.getDate();
          if (currentDay <= 7) {
            const previousMonth = new Date(currentDate);
            previousMonth.setMonth(currentDate.getMonth() - 1);
            const daysInPreviousMonth = new Date(
              startDate.getFullYear(),
              startDate.getMonth(),
              0
            ).getDate();
            if (daysInPreviousMonth == 31) {
              startDate.setDate(startDate.getDate() - 8);
            } else {
              startDate.setDate(startDate.getDate() - 7);
            }
          } else {
            startDate.setDate(startDate.getDate() - 7);
          }
          endDate.setDate(endDate.getDate());
          const previousEndDate = new Date(startDate);
          const previousStartDate = new Date(previousEndDate);
          previousStartDate.setMonth(
            previousStartDate.getMonth() - selectedMonths
          );
          let payload = {
            uid: this.currentUID,
            start: this.formatDateForPayload(dateRanges.current.start),
            end: this.formatDateForPayload(dateRanges.current.end),
            type: "aggregated_metrics",
            wids: this.selectedWIDs,
          };
          if (type == "previous") {
            payload.start = this.formatDateForPayload(
              dateRanges.previous.start
            );
            payload.end = this.formatDateForPayload(dateRanges.previous.end);
          }
          const idToken = await auth.currentUser.getIdToken();
          // check how much time it tokk to get the response
          const startTime = Date.now();
          let result;
          const response = await fetch(
            `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/refreshGmbMetrics`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${idToken}`,
              },
              body: JSON.stringify({ data: payload }),
            }
          );
          if (response.ok) {
            const endTime = Date.now();
            const duration = endTime - startTime;
            console.log(`Time taken: ${duration} milliseconds`);
            const resultData = await response.json();
            result = { data: resultData };
            // console.log("Success! Data from server fetchData:", result);
          }
          if (!this.$route.query || !this.$route.query.location) {
            this.$store.commit(
              type === "current"
                ? "SET_CURRENT_AGGREGATED_METRICS"
                : "SET_PREVIOUS_AGGREGATED_METRICS",
              {
                data: result?.data,
                monthSelection: selectedMonths,
              }
            );
          }
          if (type == "current") {
            this.rawAggregatedMetrics = result.data;
            this.computeAggregatedDataFromRaw("current");
          } else {
            this.previousRawAggregatedMetrics = result.data;
            this.computeAggregatedDataFromRaw("previous");
          }
        } catch (error) {
          console.error("Error fetching aggregated metrics:", error);
        } finally {
          this.isAggregatedDataLoading = false;
          this.previousMonthAggregatedDataLoading = false;
        }
      }
    },
    computeAggregatedDataFromRaw(type) {
      // Define base metrics structure if not already defined
      const baseMetrics = {
        desktop_maps_impressions: 0,
        mobile_maps_impressions: 0,
        desktop_search_impressions: 0,
        mobile_search_impressions: 0,
        direction_requests: 0,
        call_clicks: 0,
        website_clicks: 0,
        bookings: 0,
        food_orders: 0,
        food_menu_clicks: 0,
      };

      // Get source data based on type
      const sourceData =
        type === "previous"
          ? this.previousRawAggregatedMetrics
          : this.rawAggregatedMetrics;

      // Create fresh metrics object
      let computedMetrics =
        type === "previous"
          ? { ...baseMetrics }
          : Object.keys(baseMetrics).reduce((acc, key) => {
              acc[`total_${key}`] = 0;
              return acc;
            }, {});

      // Get the list of shallow locations from stores
      const shallowLocationIds = this.stores
        .filter((store) => store.shallowConnect)
        .map((store) => store.wid);

      // Compute fresh totals, excluding shallow locations
      sourceData?.forEach((metric) => {
        if (
          this.selectedWIDs.includes(metric.wid) &&
          !shallowLocationIds.includes(metric.wid)
        ) {
          Object.keys(baseMetrics).forEach((key) => {
            const targetKey = type === "previous" ? key : `total_${key}`;
            computedMetrics[targetKey] += metric[key] || 0;
          });
        }
      });

      // Set the computed data with fresh object
      this.$set(
        this,
        type === "previous" ? "previousAggregatedData" : "aggregatedData",
        computedMetrics
      );
      if (type == "current") {
        this.isAggregatedDataLoading = false;
      } else if (type == "previous") {
        this.previousMonthAggregatedDataLoading = false;
      }
    },

    saveMetricsPreference(metricType, selectedMetrics) {
      if (!this.$route.query || !this.$route.query.location) {
        switch (metricType) {
          case "mobile":
            this.$store.commit("SET_SELECTED_MOBILE_METRICS", selectedMetrics);
            break;
          case "desktop":
            this.$store.commit("SET_SELECTED_DESKTOP_METRICS", selectedMetrics);
            break;
          default:
            this.$store.commit("SET_SELECTED_METRICS", selectedMetrics);
        }
        this.updateChartForMetric(metricType, selectedMetrics);
      }
    },
    updateChartForMetric(metricType, selectedMetrics) {
      let chartRefName;
      let metricsModel;

      switch (metricType) {
        case "mobile":
          chartRefName = "mobileMetricsChart";
          metricsModel = this.mobileMetricsModel;
          break;
        case "desktop":
          chartRefName = "desktopMetricsChart";
          metricsModel = this.desktopMetricsModel;
          break;
        default:
          chartRefName = "metricsChart";
          metricsModel = this.availableMetrics;
      }
      this.updateChart(
        chartRefName,
        selectedMetrics,
        this.chartData,
        this.metricColors,
        metricsModel
      );
    },
    async checkPreviousMonthMetrics() {
      // Skip previous metrics if not supposed to show them
      if (!this.shouldShowPreviousMonthData) {
        this.previousMonthDataFetched = true;
        this.isChartLoading = false;
        return;
      }

      if (
        this.dataFetched ||
        (this.previousMonthMetricsInStore &&
          Object.keys(this.previousMonthMetricsInStore).length > 0)
      ) {
        let count = this.selectedWIDs.length;
        for (const wid of this.selectedWIDs) {
          // this.previousMonthAggregatedDataLoading = true;
          await this.fetchPreviousMetrics(wid);
          count--;
        }
        if (count == 0) {
          this.previousMonthDataFetched = true;
        }
      }
    },
    async refreshData() {
      this.metricsData = [];
      this.chartData = [];
      if (this.selectedWIDs.length > 0) {
        if (this.selectedDate && this.selectedDate.isCustom) {
          // For custom date ranges, data is handled by refreshDataForCustomRange
          return;
        }
        await this.getLocationStatsData("from_query");
        await this.checkPreviousMonthMetrics();
      }
    },
    isFetchedToday(wid, monthSelection) {
      const today = new Date().toLocaleDateString("en-GB"); // DD-MM-YYYY format
      const lastFetchDate =
        this.locationStatsLastFetched[wid]?.[monthSelection];
      return lastFetchDate === today;
    },
    async getLocationStatsData(fetchType) {
      // Skip this operation if no locations are selected
      if (!this.selectedWIDs || this.selectedWIDs.length === 0) {
        this.isLoading = false;
        this.isChartLoading = false;
        return;
      }

      const monthSelection = this.selectedDate.months;
      if (
        this.allFetchedLocationStatsData &&
        Object.keys(this.allFetchedLocationStatsData).length > 0
      ) {
        const hasValidData = this.selectedWIDs.every(
          (wid) =>
            this.allFetchedLocationStatsData[wid]?.[monthSelection] &&
            this.isFetchedToday(wid, monthSelection)
        );

        if (hasValidData) {
          // Keep existing data in allFetchedData and just add/update necessary data
          if (!this.allFetchedData) {
            this.allFetchedData = {};
          }
          // Update allFetchedData with data from store for all locations
          Object.keys(this.allFetchedLocationStatsData).forEach((wid) => {
            if (!this.allFetchedData[wid]) {
              this.allFetchedData[wid] = {};
            }
            if (this.allFetchedLocationStatsData[wid][monthSelection]) {
              this.allFetchedData[wid] = {
                ...this.allFetchedData[wid],
                ...this.allFetchedLocationStatsData[wid][monthSelection],
              };
            }
          });
          await this.updateMetricsAndChartData();
          this.isLoading = false;
          await this.checkPreviousMonthMetrics();
          return;
        }
      }
      // If we get here, either data doesn't exist or wasn't fetched today
      if (fetchType == "from_query") {
        this.clearCachedMetricsData();
        await this.fetchData("from_query");
      }
    },
    clearCachedMetricsData() {
      const monthSelection = this.selectedDate.months;
      // Only clear data for the specified month selection
      if (this.$store.state._allFetchedLocationStatsData) {
        this.selectedWIDs.forEach((wid) => {
          if (this.$store.state._allFetchedLocationStatsData[wid]) {
            // Only clear the current month selection
            this.$store.state._allFetchedLocationStatsData[wid][
              monthSelection
            ] = {};
          }

          if (
            this.$store.state._locationStatsLastFetched &&
            this.$store.state._locationStatsLastFetched[wid]
          ) {
            // Reset the last fetched date for this month
            this.$store.state._locationStatsLastFetched[wid][monthSelection] =
              null;
          }
        });
      }
    },

    updatePaginatedWIDs() {
      // Filter selectedWIDs to include only those that are selectable
      const selectableWIDs = this.selectedWIDs.filter((wid) => {
        const store = this.stores.find((store) => store.wid === wid);
        return store && store.isSelectable; // Ensure the store exists and is selectable
      });

      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;

      // Update paginatedWIDs with only selectable WIDs
      this.paginatedWIDs = selectableWIDs.slice(start, end);
      // Don't call updateMetricsAndChartData here to avoid infinite loops
      // console.log("this.paginatedWIDs:", this.paginatedWIDs);
    },
    nextPage(type) {
      if (type == "performance") {
        // console.log('in perf')
        if (this.currentPage < this.totalPages) {
          this.currentPage++;
          this.updatePaginatedWIDs();
          this.updateMetricsAndChartData();
        }
      }
    },
    prevPage(type) {
      // console.log('prevPage: type:', type)
      if (type == "performance") {
        // console.log('prevPage if')
        if (this.currentPage > 1) {
          this.currentPage--;
          this.updatePaginatedWIDs();
          this.updateMetricsAndChartData();
        }
      }
    },
    generateRandomPath() {
      // Generate random points for the path
      const points = [];
      const segments = 8;
      for (let i = 0; i <= segments; i++) {
        const x = (i * 100) / segments;
        const y = 150 + Math.random() * 100 - 50; // Random y value around 150
        points.push(`${x},${y}`);
      }
      return points.join(" L ");
    },
    async handleTabChange(tab) {
      this.activeTab = tab;
      this.tabIndex = tab === "metrics" ? 0 : 1;
      this.destroyChart("metricsChart");
      this.destroyChart("mobileMetricsChart");
      this.destroyChart("desktopMetricsChart");
      this.activeTab = tab;
      this.$set(this, "activeTab", tab);
      await this.updateMetricsAndChartData();
      this.$forceUpdate();
    },
    destroyChart(chartRefName) {
      if (this[chartRefName]) {
        this[chartRefName].destroy();
        this[chartRefName] = null;
      }
    },
    updateChart(
      chartRefName,
      selectedMetrics,
      chartData,
      chartColors,
      metricsModel
    ) {
      let chartRef = this[chartRefName];
      if (!chartData?.length || !this.selectedWIDs.length) {
        if (chartRef) {
          chartRef.destroy();
          this[chartRefName] = null;
        }
        return;
      }

      const ctx = this.$refs[chartRefName]?.getContext("2d");
      if (!ctx) {
        // console.log("Canvas context is not available.");
        return;
      }

      const sortedChartData = [...chartData].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
      // Function to select data points for display based on requirements
      const selectDataPointsForDisplay = (data) => {
        if (data.length === 0) return data;

        // Determine total days and months for smart labeling
        let totalDays, months;
        if (this.selectedDate.isCustom) {
          totalDays = this.selectedDate.totalDays;
          months = null;
        } else {
          months = this.selectedDate.months;
          totalDays = months * 30; // Approximate
        }

        // Apply the exact requirements:
        // 1. <= 30 days OR last 1 month: show ALL dates
        if (totalDays <= 30 || months === 1) {
          return data;
        }

        // 2. > 30 and < 90 days OR last 3/6 months: show 20 data points
        if (
          (totalDays > 30 && totalDays < 90) ||
          months === 3 ||
          months === 6
        ) {
          return this.selectEvenly(data, 20);
        }

        // 3. >= 90 days OR last 12 months: show 40 data points
        if (totalDays >= 90 || months === 12) {
          return this.selectEvenly(data, 40);
        }

        // Default: return all data
        return data;
      };

      const createGradient = (color) => {
        const gradient = ctx.createLinearGradient(0, 0, 0, 400);
        gradient.addColorStop(0, `${color}20`);
        gradient.addColorStop(1, `${color}00`);
        return gradient;
      };

      // Select data points for display (this will be the same for all metrics)
      const displayData = selectDataPointsForDisplay(sortedChartData);

      const datasets = selectedMetrics.map((metric) => {
        return {
          label: metricsModel.find((m) => m.value === metric)?.text || metric,
          data: displayData.map((day) => day[metric]),
          borderColor: chartColors[metric],
          backgroundColor: createGradient(chartColors[metric]),
          borderWidth: 2,
          pointBackgroundColor: chartColors[metric],
          pointBorderColor: "#FFFFFF",
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointHoverBorderWidth: 3,
          pointHoverBackgroundColor: "#FFFFFF",
          pointHoverBorderColor: chartColors[metric],
          tension: 0.3,
          fill: true,
        };
      });
      // Generate labels based on display data
      let totalDays, months;
      if (this.selectedDate.isCustom) {
        totalDays = this.selectedDate.totalDays;
        months = null;
      } else {
        months = this.selectedDate.months;
        totalDays = months * 30;
      }

      let labels;
      if (totalDays <= 30 || months === 1) {
        // Show all dates with day and month
        labels = displayData.map((day) =>
          new Date(day.date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })
        );
      } else {
        // For filtered data, show strategic labels
        labels = displayData.map((day, index) => {
          const date = new Date(day.date);
          const prevDate =
            index > 0 ? new Date(displayData[index - 1].date) : null;

          const isStartOfMonth =
            date.getDate() === 1 ||
            (prevDate && date.getMonth() !== prevDate.getMonth());

          const isFirstDataPoint = index === 0;
          const isLastDataPoint = index === displayData.length - 1;

          // Show labels for start of month, first point, and last point
          if (isFirstDataPoint || isStartOfMonth || isLastDataPoint) {
            return date.toLocaleDateString("en-US", {
              month: "short",
              year: totalDays >= 90 || months === 12 ? "numeric" : undefined,
            });
          }
          return "";
        });
      }
      // console.log('labels:', labels)
      const config = {
        type: "line",
        data: {
          labels,
          datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: "index",
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              backgroundColor: "rgba(255, 255, 255, 0.98)",
              titleColor: "#1a202c",
              bodyColor: "#4a5568",
              titleFont: {
                size: 14,
                weight: "bold",
                family: "'Inter', sans-serif",
              },
              bodyFont: {
                size: 13,
                family: "'Inter', sans-serif",
              },
              padding: 12,
              borderColor: "rgba(0,0,0,0.1)",
              borderWidth: 1,
              cornerRadius: 8,
              displayColors: true,
              boxWidth: 8,
              boxHeight: 8,
              boxPadding: 4,
              usePointStyle: true,
              callbacks: {
                title: function (tooltipItems) {
                  const date = new Date(
                    displayData[tooltipItems[0].dataIndex].date
                  );
                  return date.toLocaleDateString("en-US", {
                    // weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  });
                },
                label: function (context) {
                  let label = context.dataset.label || "";
                  if (label) {
                    label += ": ";
                  }
                  if (context.parsed.y !== null) {
                    label += context.parsed.y.toLocaleString();
                  }
                  return label;
                },
              },
            },
          },
          scales: {
            x: {
              grid: {
                display: false,
              },
              ticks: {
                font: {
                  size: 12,
                  family: "'Inter', sans-serif",
                },
                color: "#718096",
              },
            },
            y: {
              beginAtZero: true,
              grid: {
                color: "rgba(0,0,0,0.05)",
                drawBorder: false,
              },
              ticks: {
                font: {
                  size: 12,
                  family: "'Inter', sans-serif",
                },
                color: "#718096",
                padding: 8,
                callback: function (value) {
                  if (value >= 1000000) {
                    return (value / 1000000).toFixed(1) + "M";
                  } else if (value >= 1000) {
                    return (value / 1000).toFixed(1) + "K";
                  }
                  return value;
                },
              },
            },
          },
          layout: {
            padding: {
              top: 20,
              right: 20,
              bottom: 20,
              left: 20,
            },
          },
        },
      };
      if (chartRef) {
        chartRef.destroy();
      }
      this[chartRefName] = new Chart(ctx, config);
    },
    updateAllCharts() {
      this.updateChart(
        "metricsChart",
        this.selectedMetricsModel,
        this.chartData,
        this.metricColors,
        this.availableMetrics
      );
      this.updateChart(
        "mobileMetricsChart",
        this.selectedMobileMetricsModel,
        this.chartData,
        this.metricColors,
        this.mobileMetricsModel
      );
      this.updateChart(
        "desktopMetricsChart",
        this.selectedDesktopMetricsModel,
        this.chartData,
        this.metricColors,
        this.desktopMetricsModel
      );
    },

    generateDateOptions() {
      this.dateOptions = [
        {
          text: "Last 1 Month",
          value: {
            months: 1,
          },
        },
        {
          text: "Last 3 Months",
          value: {
            months: 3,
          },
        },
        {
          text: "Last 6 Months",
          value: {
            months: 6,
          },
        },
        {
          text: "Last 1 Year",
          value: {
            months: 12,
          },
        },
      ];
      if (!this.selectedStatsMonth) {
        this.selectedDate = this.dateOptions[0].value;
        if (!this.$route.query || !this.$route.query.location) {
          this.$store.commit(
            "SET_SELECTED_STATS_MONTH",
            this.selectedDate.months
          );
        }
      }
    },
    sortData(column) {
      // If clicking the same column, reverse the sort direction
      if (this.sortBy === column) {
        this.sortDesc = !this.sortDesc;
      } else {
        // New column, set it as sort key and default to ascending
        this.sortBy = column;
        this.sortDesc = false;
      }
      console.log("metricsData:", this.metricsData);
      // Sort the metricsData array
      this.metricsData.sort((a, b) => {
        let aVal = column === "location" ? a.location.sub_locality : a[column];
        let bVal = column === "location" ? b.location.sub_locality : b[column];

        // Handle numeric values
        if (typeof aVal === "number" && typeof bVal === "number") {
          return this.sortDesc ? bVal - aVal : aVal - bVal;
        }

        // Handle string values
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
        if (aVal < bVal) return this.sortDesc ? 1 : -1;
        if (aVal > bVal) return this.sortDesc ? -1 : 1;
        return 0;
      });
    },
    async fetchGMBLogo() {
      if (this.gmb_logo) {
        return;
      }
      try {
        const platformDoc = await getDoc(
          doc(db, "fly_platforms", "google_business_profile")
        );
        if (platformDoc.exists()) {
          const logoUrl = platformDoc.data().logo_url;
          this.gmb_logo = logoUrl;
        }
      } catch (error) {
        console.error("Error fetching platform logo:", error);
      }
    },
    getMetricChange(metricType) {
      if (
        !this.previousAggregatedData ||
        Object.keys(this.previousAggregatedData)?.length === 0 ||
        this.previousMonthAggregatedDataLoading
      ) {
        return null;
      }
      let currentMetric = 0;
      let previousMetric = 0;
      try {
        switch (metricType) {
          case "maps":
            previousMetric =
              (this.previousAggregatedData.desktop_maps_impressions || 0) +
              (this.previousAggregatedData.mobile_maps_impressions || 0);
            currentMetric =
              (this.aggregatedData.total_desktop_maps_impressions || 0) +
              (this.aggregatedData.total_mobile_maps_impressions || 0);
            break;
          case "search":
            previousMetric =
              (this.previousAggregatedData.desktop_search_impressions || 0) +
              (this.previousAggregatedData.mobile_search_impressions || 0);
            currentMetric =
              (this.aggregatedData.total_desktop_search_impressions || 0) +
              (this.aggregatedData.total_mobile_search_impressions || 0);
            break;
          case "directions":
            previousMetric =
              this.previousAggregatedData.direction_requests || 0;
            currentMetric = this.aggregatedData.total_direction_requests || 0;
            break;
          case "calls":
            previousMetric = this.previousAggregatedData.call_clicks;
            currentMetric = this.aggregatedData.total_call_clicks;
            break;
          case "website":
            previousMetric = this.previousAggregatedData.website_clicks;
            currentMetric = this.aggregatedData.total_website_clicks;
            break;
          case "bookings":
            previousMetric = this.previousAggregatedData.bookings;
            currentMetric = this.aggregatedData.total_bookings;
            break;
          case "food_orders":
            previousMetric = this.previousAggregatedData.food_orders;
            currentMetric = this.aggregatedData.total_food_orders;
            break;
          case "food_menu":
            previousMetric = this.previousAggregatedData.food_menu_clicks;
            currentMetric = this.aggregatedData.total_food_menu_clicks;
            break;
          default:
            return null;
        }
        // Validate metrics before calculation
        if (previousMetric === undefined || currentMetric === undefined) {
          return null;
        }
        return this.calculatePercentageChange(currentMetric, previousMetric);
      } catch (error) {
        console.error(
          `Error calculating metric change for ${metricType}:`,
          error
        );
        return null;
      }
    },

    getTotalMetric(metricType) {
      // If no data is available, return null
      // console.log('this.chartData:',Array.isArray(this.chartData),  this.chartData)
      if (
        (!this.metricsData || !Array.isArray(this.metricsData)) &&
        (!this.chartData || !Array.isArray(this.chartData))
      )
        return null;

      // For metrics tab (time series data)
      if (this.activeTab === "metrics" && this.chartData?.length) {
        // Sum up values across all dates for the selected metric type
        return this.chartData.reduce((total, dayData) => {
          switch (metricType) {
            case "maps":
              return (
                total +
                (parseInt(dayData.desktop_maps_impressions) || 0) +
                (parseInt(dayData.mobile_maps_impressions) || 0)
              );
            case "search":
              return (
                total +
                (parseInt(dayData.desktop_search_impressions) || 0) +
                (parseInt(dayData.mobile_search_impressions) || 0)
              );
            case "directions":
              return total + (parseInt(dayData.direction_requests) || 0);
            case "calls":
              return total + (parseInt(dayData.call_clicks) || 0);
            case "website":
              return total + (parseInt(dayData.website_clicks) || 0);
            case "bookings":
              return total + (parseInt(dayData.bookings) || 0);
            case "food_orders":
              return total + (parseInt(dayData.food_orders) || 0);
            case "food_menu":
              return total + (parseInt(dayData.food_menu_clicks) || 0);
            default:
              return total;
          }
        }, 0);
      }

      // For performance tab (location-based data)
      if (this.activeTab === "performance" && Array.isArray(this.metricsData)) {
        return this.metricsData.reduce((total, metric) => {
          if (!metric) return total;

          switch (metricType) {
            case "maps":
              return (
                total +
                (parseInt(metric.desktop_maps_impressions) || 0) +
                (parseInt(metric.mobile_maps_impressions) || 0)
              );
            case "search":
              return (
                total +
                (parseInt(metric.desktop_search_impressions) || 0) +
                (parseInt(metric.mobile_search_impressions) || 0)
              );
            case "directions":
              return total + (parseInt(metric.direction_requests) || 0);
            case "calls":
              return total + (parseInt(metric.call_clicks) || 0);
            case "website":
              return total + (parseInt(metric.website_clicks) || 0);
            case "bookings":
              return total + (parseInt(metric.bookings) || 0);
            case "food_orders":
              return total + (parseInt(metric.food_orders) || 0);
            case "food_menu":
              return total + (parseInt(metric.food_menu_clicks) || 0);
            default:
              return total;
          }
        }, 0);
      }

      return 0;
    },
    getTooltipMessage(metric, type = "default") {
      const messages = {
        maps: {
          desktop: "Business impressions on Google Maps on Desktop devices",
          mobile: "Business impressions on Google Maps on Mobile devices",
          combined: "Total Maps impressions across all locations",
        },
        search: {
          desktop: "Business impressions on Google Search on Desktop devices",
          mobile: "Business impressions on Google Search on Mobile devices",
          combined: "Total Search impressions across all locations",
        },
        directions: {
          default: "Total direction requests across all locations",
          combined: "Total direction requests across all locations",
        },
        calls: {
          default:
            "The number of times the business profile call button was clicked",
          combined: "Total call clicks across all locations",
        },
        website: {
          default:
            "The number of times the business profile website was clicked",
          combined: "Total website clicks across all locations",
        },
        bookings: {
          default:
            "The number of bookings made from the business profile via Reserve with Google",
          combined: "Total bookings across all locations",
        },
        food_orders: {
          default:
            "The number of food orders received from the business profile",
          combined: "Total food orders across all locations",
        },
        food_menu: {
          default:
            "The number of clicks to view or interact with the menu content on the business profile",
          combined:
            "Total number of clicks to interact with the menu content across all locations",
        },
      };

      // Return appropriate message based on metric and type
      if (type === "desktop") {
        return messages[metric]?.desktop || messages[metric]?.default;
      } else if (type === "mobile") {
        return messages[metric]?.mobile || messages[metric]?.default;
      } else if (type === "combined") {
        return messages[metric]?.combined || messages[metric]?.default;
      } else {
        return messages[metric]?.default;
      }
    },
    handleClickOutside(event) {
      const profileDropdown =
        this.$refs.showStoreDropdown?.$refs.showStoreDropdown;
      if (profileDropdown && !profileDropdown.contains(event.target)) {
        this.closeProfileDropdown();
      }
    },
    closeProfileDropdown() {
      if (this.showStoreDropdown) {
        this.showStoreDropdown = false;
      }
    },
    toggleStoreDropdown(event) {
      this.showStoreDropdown = !this.showStoreDropdown;
      event.stopPropagation();
    },
    async handleLocationChange(profile) {
      this.selectedWIDs = profile.filter((item) =>
        this.stores.some((l) => l.wid === item && l.isSelectable)
      );
      // Update checked state for all stores
      this.stores.forEach((store) => {
        if (store.isSelectable) {
          store.checked = this.selectedWIDs.includes(store.wid);
          this.fetchPreviousMetrics(store.wid);
        }
      });
      // Update the URL with the selected locations
      let query = { ...this.$route.query };
      if (this.selectedWIDs.length > 0) {
        query.location = this.selectedWIDs.join(",");
      } else {
        delete query.location;
      }
      this.$router.replace({ query });
      console.log("this.selectedWIDs orig:", this.selectedWIDs);
      if (!query || !query.location) {
        this.$store.commit("SET_SELECTED_STATS_LOCATIONS", this.selectedWIDs);
      }
      this.showStoreDropdown = false;
      // Clear chart data if no locations selected
      if (this.selectedWIDs.length === 0) {
        this.chartData = [];
        this.aggregatedData = {
          total_desktop_maps_impressions: null,
          total_mobile_maps_impressions: null,
          total_desktop_search_impressions: null,
          total_mobile_search_impressions: null,
          total_direction_requests: null,
          total_call_clicks: null,
          total_website_clicks: null,
          total_bookings: null,
          total_food_orders: null,
          total_food_menu_clicks: null,
        };
        this.destroyChart("metricsChart");
        this.destroyChart("mobileMetricsChart");
        this.destroyChart("desktopMetricsChart");
        return; // Don't fetch data if no locations selected
      }
      await Promise.all([
        this.aggregatedMetrics("current"),
        this.aggregatedMetrics("previous"),
      ]);
      this.updatePaginatedWIDs();
    },
    calculateDateRanges(selectedMonths) {
      const today = new Date();
      const currentDay = today.getDate();

      // Determine how many days to subtract based on current day and previous month
      let daysToSubtract = 7;
      if (currentDay <= 7) {
        const previousMonth = new Date(today);
        previousMonth.setMonth(previousMonth.getMonth() - 1);
        const daysInPreviousMonth = new Date(
          previousMonth.getFullYear(),
          previousMonth.getMonth() + 1,
          0
        ).getDate();
        daysToSubtract = daysInPreviousMonth === 31 ? 8 : 7;
      }

      // Map months to days for consistency:
      // - Last 1 month = Last 30 days
      // - Last 3 months = Last 90 days
      // - Last 6 months = Last 180 days
      // - Last 12 months = Last 365 days
      const daysMap = {
        1: 30,
        3: 90,
        6: 180,
        12: 365
      };
      const totalDays = daysMap[selectedMonths] || selectedMonths * 30;

      // Calculate current period end date (today minus daysToSubtract)
      const adjustedCurrentEndDate = new Date(today);
      adjustedCurrentEndDate.setDate(adjustedCurrentEndDate.getDate() - daysToSubtract);

      // Calculate current period start date using day-based calculation
      const adjustedCurrentStartDate = new Date(adjustedCurrentEndDate);
      adjustedCurrentStartDate.setDate(adjustedCurrentStartDate.getDate() - totalDays + 1); // +1 for inclusive counting

      // Create pure date objects for previous period calculation
      const startDateOnly = new Date(
        adjustedCurrentStartDate.getFullYear(),
        adjustedCurrentStartDate.getMonth(),
        adjustedCurrentStartDate.getDate()
      );
      const endDateOnly = new Date(
        adjustedCurrentEndDate.getFullYear(),
        adjustedCurrentEndDate.getMonth(),
        adjustedCurrentEndDate.getDate()
      );

      // Calculate range days for previous period (using same logic as custom range)
      const rangeDays = this.safeDateDifference(startDateOnly, endDateOnly);

      // Calculate previous period - same logic as custom date range
      // Previous range ends the day before current range starts
      const previousEndDate = this.safeSubtractDays(startDateOnly, 1);
      // Calculate previous range start
      let previousStartDate;
      if (rangeDays === 1) {
        // For 1-day range, previous start = previous end
        previousStartDate = new Date(previousEndDate);
      } else {
        // For multi-day ranges, calculate based on duration
        previousStartDate = this.safeSubtractDays(previousEndDate, rangeDays - 2);
      }
      // Set proper time boundaries
      // Start dates: beginning of the day (00:00:00.000)
      adjustedCurrentStartDate.setHours(0, 0, 0, 0);
      previousStartDate.setHours(0, 0, 0, 0);

      // End dates: end of the day (23:59:59.999)
      adjustedCurrentEndDate.setHours(23, 59, 59, 999);
      previousEndDate.setHours(23, 59, 59, 999);

      return {
        current: {
          start: adjustedCurrentStartDate,
          end: adjustedCurrentEndDate,
        },
        previous: {
          start: previousStartDate,
          end: previousEndDate,
        },
      };
    },
    formatDateForPayload(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    async fetchData(fetch_type) {
      try {
        if (this.selectedWIDs.length === 0) {
          this.isChartLoading = false;
          this.isLoading = false;
          return;
        }
        if (fetch_type) {
          this.showLoadingMessage = false;
          this.isChartLoading = true;
          // this.$loader.show("Fetching stats, please wait...");
        }
        if (this.chartData?.length && this.activeTab == "metrics") {
          return this.chartData;
        } else if (
          this.metricsData?.length &&
          this.activeTab == "performance"
        ) {
          return this.metricsData;
        }
        this.metricsData = [];
        this.chartData = [];
        this.allFetchedData = {};
        this.dataFetched = false;
        this.isLoading = true;

        // Skip fetchData for custom date ranges - they use different method
        if (this.selectedDate && this.selectedDate.isCustom) {
          return;
        }

        const selectedMonths = this.selectedDate?.months;
        let wids = this.selectedWIDs;

        let startDate = new Date();
        let endDate = new Date();
        startDate.setMonth(startDate.getMonth() - selectedMonths);
        const currentDate = new Date();
        const currentDay = startDate.getDate();
        if (currentDay <= 7) {
          const previousMonth = new Date(currentDate);
          previousMonth.setMonth(currentDate.getMonth() - 1);
          const daysInPreviousMonth = new Date(
            startDate.getFullYear(),
            startDate.getMonth(),
            0
          ).getDate();
          if (daysInPreviousMonth == 31) {
            startDate.setDate(startDate.getDate() - 8);
          } else {
            startDate.setDate(startDate.getDate() - 7);
          }
        } else {
          startDate.setDate(startDate.getDate() - 7);
        }
        // Set endDate to yesterday
        endDate.setDate(endDate.getDate());

        const previousEndDate = new Date(startDate);
        const previousStartDate = new Date(previousEndDate);
        previousStartDate.setMonth(
          previousStartDate.getMonth() - selectedMonths
        );
        const dateRanges = this.calculateDateRanges(selectedMonths);

        const connectedAppsPromises = wids?.map(async (wid) => {
          const connectedAppsRef = collection(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            wid,
            "connected-apps"
          );
          const gbpDoc = await getDocs(
            query(
              connectedAppsRef,
              where("__name__", "==", "google_business_profile")
            )
          );
          return { wid, gbpDoc };
        });

        const payload = {
          uid: this.currentUID,
          wids: wids,
          months: selectedMonths,
          start_date: this.formatDateForPayload(dateRanges.current.start),
          end_date: this.formatDateForPayload(dateRanges.current.end),
          type: fetch_type || "",
          previous_start_date: previousStartDate,
        };

        const connectedAppsResults = await Promise.all(connectedAppsPromises);
        // Process results and build payloads

        connectedAppsResults.forEach(({ wid, gbpDoc }) => {
          if (gbpDoc.empty) return;

          const gbpData = gbpDoc.docs[0].data();
          const accounts = gbpData.accounts;
          if (!accounts?.length) return;

          const locations = accounts[0].locations;
          if (!locations?.length) return;
          const location = locations[0];

          this.locationTitlesMapping[wid] = {
            title: location.title,
            locationId: location.name,
            wid,
          };
        });
        const idToken = await auth.currentUser.getIdToken();
        // check how much time it tokk to get the response
        const startTime = Date.now();
        let result;
        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/refreshGmbMetrics`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ data: payload }),
          }
        );
        if (response.ok) {
          const endTime = Date.now();
          const duration = endTime - startTime;
          console.log(`Time taken: ${duration} milliseconds`);
          const resultData = await response.json();
          result = { data: resultData };
          // console.log("Success! Data from server fetchData:", result);
        }
        console.log("fetchPlatformMetrics result:", result);
        this.dataFetched = result.data[0].status || false;

        result.data?.forEach((item) => {
          const detailedData = item?.data?.detailedData || [];

          detailedData.forEach((metric) => {
            // const locationId = metric.location_id.split("/").pop();
            if (!this.allFetchedData[metric.wid]) {
              this.allFetchedData[metric.wid] = {};
            }
            const dateKey = metric?.date?.value;
            // Store data nested by date
            if (!this.allFetchedData[metric.wid][dateKey]) {
              this.$set(this.allFetchedData[metric.wid], dateKey, {});
            }
            this.allFetchedData[metric.wid][dateKey] =
              this.extractMetrics(metric);
            this.$store.commit("SET_ALL_FETCHED_LOCATION_STATS_DATA", {
              wid: metric.wid,
              monthSelection: selectedMonths,
              date: dateKey,
              data: this.extractMetrics(metric),
            });
          });
        });

        console.log("this.allFetchedData::", this.allFetchedData);
        if (result.data[0].status) {
          await this.updateMetricsAndChartData();
        }

        // this.sortData("location");
        // if (this.activeTab == "performance") {
        // }
      } catch (error) {
        console.error("Error fetching metrics:", error);
        this.handleError(error);
      } finally {
        this.isLoading = false;
        if (fetch_type) this.$loader.hide();
        this.isChartLoading = false;
      }
    },
    async updateMetricsAndChartData() {
      this.metricsData = [];
      this.chartData = [];
      this.$set(this, "chartData", []);

      // Calculate date range based on whether it's custom or preset
      let startDate, endDate;
      if (this.selectedDate?.isCustom) {
        startDate = new Date(this.selectedDate.startDate);
        endDate = new Date(this.selectedDate.endDate);
      } else {
        const dateRanges = this.calculateDateRanges(this.selectedDate.months);
        startDate = dateRanges.current.start;
        endDate = dateRanges.current.end;
      }
      // Reset aggregated data with same structure

      // Create Map for faster lookups
      const storesMap = new Map(this.stores.map((store) => [store.wid, store]));
      const dateWiseMetrics = {};
      // Process aggregated data for all selected locations

      // Process metrics for paginated locations
      await Promise.all(
        this.paginatedWIDs.map(async (wid) => {
          if (!this.allFetchedData[wid]) return;

          const locationData = this.allFetchedData[wid];
          const store = storesMap.get(wid) || {};

          // Calculate filtered metrics maintaining same structure
          const filteredMetrics = Object.keys(locationData)
            .filter((date) => {
              const dateObj = new Date(date);
              return dateObj >= startDate && dateObj <= endDate;
            })
            .reduce((acc, date) => {
              const dailyMetrics = locationData[date];
              Object.keys(dailyMetrics).forEach((key) => {
                acc[key] = (acc[key] || 0) + (dailyMetrics[key] || 0);
              });
              return acc;
            }, {});

          // Create metrics object with same structure
          const metrics = {
            location: {
              wid: wid,
              sub_locality: store.sub_locality || "",
              city: store.city || "",
              state: store.state || "",
              storeLocation: store.storeLocation || "",
              name:
                store.name ||
                this.stores.find((store) => store.wid === wid)?.title ||
                "",
              title: this.locationTitlesMapping[wid]?.title || "",
              short_address: store.short_address || "",
            },
            ...filteredMetrics,
          };

          this.metricsData.push(metrics);

          // Process date-wise metrics maintaining same structure
          Object.keys(locationData).forEach((date) => {
            const dateObj = new Date(date);
            if (dateObj >= startDate && dateObj <= endDate) {
              if (!dateWiseMetrics[date]) {
                dateWiseMetrics[date] = {
                  date,
                  desktop_maps_impressions: 0,
                  mobile_maps_impressions: 0,
                  desktop_search_impressions: 0,
                  mobile_search_impressions: 0,
                  direction_requests: 0,
                  call_clicks: 0,
                  website_clicks: 0,
                  bookings: 0,
                  food_orders: 0,
                  food_menu_clicks: 0,
                };
              }

              const dailyMetrics = locationData[date];
              dateWiseMetrics[date].desktop_maps_impressions +=
                dailyMetrics.desktop_maps_impressions || 0;
              dateWiseMetrics[date].mobile_maps_impressions +=
                dailyMetrics.mobile_maps_impressions || 0;
              dateWiseMetrics[date].desktop_search_impressions +=
                dailyMetrics.desktop_search_impressions || 0;
              dateWiseMetrics[date].mobile_search_impressions +=
                dailyMetrics.mobile_search_impressions || 0;
              dateWiseMetrics[date].direction_requests +=
                dailyMetrics.direction_requests || 0;
              dateWiseMetrics[date].call_clicks +=
                dailyMetrics.call_clicks || 0;
              dateWiseMetrics[date].website_clicks +=
                dailyMetrics.website_clicks || 0;
              dateWiseMetrics[date].bookings += dailyMetrics.bookings || 0;
              dateWiseMetrics[date].food_orders +=
                dailyMetrics.food_orders || 0;
              dateWiseMetrics[date].food_menu_clicks +=
                dailyMetrics.food_menu_clicks || 0;
            }
          });
        })
      );

      // Convert dateWiseMetrics to chartData maintaining same structure
      this.chartData = Object.keys(dateWiseMetrics).map((date) => ({
        date,
        ...dateWiseMetrics[date],
      }));

      // Ensure reactivity while maintaining data structure
      await this.$nextTick();
      this.$set(this, "metricsData", [...this.metricsData]);
      this.$set(this, "chartData", [...this.chartData]);

      await this.updateAllCharts();
      this.isChartLoading = false;
    },

    async fetchStores() {
      const uid = this.currentUID;
      const storeRef = collection(db, "users", uid, "fly_workspaces");

      // Fetch store documents once
      const querySnapshot = await getDocs(storeRef);
      this.stores = [];
      this.selectedWIDs = [];
      // Check for location query parameter
      const locationParam = this.$route.query.location;
      const locationIds = locationParam
        ? Array.isArray(locationParam)
          ? locationParam
          : [locationParam]
        : [];

      // Convert to array if it's a comma-separated string (e.g., "id1,id2,id3")
      const locationIdsArray =
        locationIds.length === 1 && locationIds[0].includes(",")
          ? locationIds[0].split(",").map((id) => id.trim())
          : locationIds;

      // Process each store document
      const storePromises = querySnapshot.docs.map(async (doc) => {
        let storeData = doc.data();
        if (
          storeData.storeWorkspace &&
          storeData.admin === this.WID &&
          (storeData.connected_apps?.includes("google_business_profile") ||
            storeData.shallowConnect)
        ) {
          storeData.wid = doc.id;
          storeData.type = "store";
          storeData.display_name = `${storeData.sub_locality} - ${storeData.name}`;
          let verificationState = null;
          if (
            !storeData.shallowConnect &&
            storeData.connected_apps?.includes("google_business_profile")
          ) {
            const connectedAppsRef = collection(
              db,
              "users",
              uid,
              "fly_workspaces",
              doc.id,
              "connected-apps"
            );
            const gbpDoc = await getDocs(
              query(
                connectedAppsRef,
                where("__name__", "==", "google_business_profile")
              )
            );
            // Check verification state in workspace doc first
            if (storeData.verificationDetails) {
              verificationState =
                storeData.verificationDetails?.verification_state;
            } else if (!gbpDoc.empty) {
              const gbpData = gbpDoc.docs[0].data();
              const accounts = gbpData.accounts;
              // Check verification state of the first account and first location
              if (accounts.length > 0 && accounts[0].locations.length > 0) {
                verificationState = accounts[0].locations[0].verification_state;
              }
            }
            if (verificationState && verificationState !== "COMPLETED") {
              storeData.isSelectable = false; // Mark as non-selectable
              storeData.checked = false;
              storeData.tooltipMessage = "This location is not verified";
            } else {
              // prev code:
              /*
              storeData.isSelectable = true;
              // Only set checked if it's in selectedStatsLocations
              storeData.checked = this.selectedStatsLocations?.includes(doc.id) || false;
              
              // Add to selectedWIDs only if no previous selection exists
              if (!this.selectedStatsLocations?.length) {
                this.selectedWIDs.push(doc.id);
                storeData.checked = true;
              */
              storeData.isSelectable = true;
              // Determine if this location should be checked based on URL parameters
              if (locationIdsArray.length > 0) {
                // Only check it if it's in the URL parameters
                storeData.checked = locationIdsArray.includes(doc.id);
                if (storeData.checked) {
                  this.selectedWIDs.push(doc.id);
                }
              } else if (this.selectedStatsLocations?.length) {
                // Or if it's in selectedStatsLocations
                storeData.checked = this.selectedStatsLocations.includes(
                  doc.id
                );
                // Add to selectedWIDs if it's checked
                if (storeData.checked) {
                  this.selectedWIDs.push(doc.id);
                }
              } else {
                // Default behavior - select all
                storeData.checked = true;
                this.selectedWIDs.push(doc.id);
              }
            }
          }
          if (storeData.shallowConnect) {
            storeData.checked = false;
            storeData.tooltipMessage =
              "Connect Google Business Profile to view analytics";
            storeData.isSelectable = false; // Mark as non-selectable
          }
          this.stores.push(storeData);

          // Initialize showPreviousData to true for this store
          this.$set(this.showPreviousData, doc.id, true);
        }
      });

      // Wait for all store promises to resolve
      await Promise.all(storePromises);
      // prev code:
      /*
      if(this.selectedStatsLocations?.length) {
        this.selectedWIDs = [...this.selectedStatsLocations];
          // Update checked state for all stores
          this.stores.forEach(store => {
            if (store.isSelectable) {
              store.checked = this.selectedStatsLocations.includes(store.wid);
            }
          });      
        }
      */
      // const tooltipMessages = [];
      this.stores.forEach((store) => {
        // if(!store.shallowConnect || store.shallowConnect == undefined) {
        store.checked = locationIdsArray.includes(store.wid);
        // Check for the tooltip message for the wid in query param
        if (locationIdsArray.includes(store.wid) && store.tooltipMessage) {
          this.$snackbar.show({
            // message: `${store.sub_locality || store.name}: ${store.tooltipMessage}`,
            message: `${store.tooltipMessage}`,
            color: "error",
          });
        }
        // }
      });
      // // Display tooltip messages in snackbar if any exist
      // if(tooltipMessages.length > 0) {
      //   // If multiple messages, combine them or just show the first one
      //   if(tooltipMessages.length === 1) {
      //     this.$snackbar.show({
      //       message: `${tooltipMessages[0].name}: ${tooltipMessages[0].message}`,
      //       color: 'error'
      //     });
      //   } else {
      //     const locationNames = tooltipMessages.map(item => item.name).join(', ');
      //     this.$snackbar.show({
      //       message: `Some selected locations have important messages. Check location details.`,
      //       color: 'info'
      //     });
      //   }
      // }
      if (!this.$route.query || !this.$route.query.location) {
        this.$store.commit("SET_SELECTED_STATS_LOCATIONS", this.selectedWIDs);
      }
      this.updatePaginatedWIDs();
    },
    async selectDateRange() {
      // If user selects a preset range while custom range is active, clear custom range
      if (
        this.isCustomDateRange &&
        this.selectedDate &&
        !this.selectedDate.isCustom
      ) {
        this.isCustomDateRange = false;
        this.customStartDate = null;
        this.customEndDate = null;
      }

      if (!this.$route.query || !this.$route.query.location) {
        // Only store in vuex if it's not a custom range
        if (this.selectedDate && !this.selectedDate.isCustom) {
          this.$store.commit(
            "SET_SELECTED_STATS_MONTH",
            this.selectedDate.months
          );
        }
      }
      this.previousAggregatedData = {};
      this.previousMonthDataFetched = false;
      this.metricsData = [];
      this.chartData = [];
      if (!this.selectedDate) return;
      this.refreshData();

      // Reset and reopen all dropdowns
      this.selectedWIDs.forEach((wid) => {
        this.$set(this.showPreviousData, wid, true);
      });
    },

    // Custom date range methods
    closeCustomDateRange() {
      this.showCustomDateRange = false;
      this.customStartDate = null;
      this.customEndDate = null;
      this.dateValidationError = null;
    },

    clearCustomDateRange() {
      // Reset custom date range variables
      this.isCustomDateRange = false;
      this.customStartDate = null;
      this.customEndDate = null;
      this.showCustomDateRange = false;
      this.dateValidationError = null;

      // Switch to "Last 1 Month" selection
      this.selectedDate = this.dateOptions[0].value; // First option is "Last 1 Month"

      // Store the selection if not from route query
      if (!this.$route.query || !this.$route.query.location) {
        this.$store.commit(
          "SET_SELECTED_STATS_MONTH",
          this.selectedDate.months
        );
      }

      // Reset and refresh data
      this.previousAggregatedData = {};
      this.previousMonthDataFetched = false;
      this.metricsData = [];
      this.chartData = [];

      // Refresh data for the new date range
      this.refreshData();

      // Reset and reopen all dropdowns
      this.selectedWIDs.forEach((wid) => {
        this.$set(this.showPreviousData, wid, true);
      });
    },

    validateCustomDates() {
      this.dateValidationError = null;

      if (!this.customStartDate || !this.customEndDate) {
        return;
      }

      const start = new Date(this.customStartDate);
      const end = new Date(this.customEndDate);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // Set to end of today

      if (start > end) {
        this.dateValidationError = "Start date must be before end date";
        return;
      }

      if (end > today) {
        this.dateValidationError = "End date cannot be in the future";
        return;
      }

      // Check against GMB stats start date if available
      if (this.gmbStatsStartDate) {
        const gmbStart = new Date(this.gmbStatsStartDate);
        if (start < gmbStart) {
          const formattedMinDate = gmbStart.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
          this.dateValidationError = `Start date cannot be before ${formattedMinDate}`;
          return;
        }
      }

      // Check if date range is too far in the past (optional - you can adjust this)
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      if (start < twoYearsAgo) {
        this.dateValidationError = "Start date cannot be more than 2 years ago";
        return;
      }
    },

    setQuickCustomRange(days) {
      // Use the same date calculation logic as preset months to maintain consistent x-axis labeling
      const today = new Date();
      const currentDay = today.getDate();

      // Determine how many days to subtract based on current day (same logic as calculateDateRanges)
      let daysToSubtract = 7;
      if (currentDay <= 7) {
        const previousMonth = new Date(today);
        previousMonth.setMonth(previousMonth.getMonth() - 1);
        const daysInPreviousMonth = new Date(
          previousMonth.getFullYear(),
          previousMonth.getMonth() + 1,
          0
        ).getDate();
        daysToSubtract = daysInPreviousMonth === 31 ? 8 : 7;
      }

      // Calculate end date (same as preset months)
      const adjustedEndDate = new Date(today);
      adjustedEndDate.setDate(adjustedEndDate.getDate() - daysToSubtract);

      // Calculate start date
      const adjustedStartDate = new Date(adjustedEndDate);
      adjustedStartDate.setDate(adjustedStartDate.getDate() - days + 1); // +1 for inclusive counting

      this.customStartDate = this.formatDateToYMD(adjustedStartDate);
      this.customEndDate = this.formatDateToYMD(adjustedEndDate);
      this.validateCustomDates();
    },

    setCurrentCustomMonth() {
      const today = new Date();
      const dayOfMonth = today.getDate();

      // Start date: Always first day of current month
      const startDate = new Date(today.getFullYear(), today.getMonth(), 1);

      let endDate;
      if (dayOfMonth > 7) {
        // If today > 7th, end date is today minus 7 days
        endDate = new Date(
          today.getFullYear(),
          today.getMonth(),
          dayOfMonth - 7
        );
      } else {
        // If today <= 7th, this shouldn't happen as button should be disabled
        // But as fallback, use first day of month
        endDate = new Date(today.getFullYear(), today.getMonth(), 1);
      }

      // Format dates to YYYY-MM-DD to avoid timezone issues
      this.customStartDate = this.formatDateToYMD(startDate);
      this.customEndDate = this.formatDateToYMD(endDate);
      this.validateCustomDates();
    },

    // Helper method to format date to YYYY-MM-DD without timezone issues
    formatDateToYMD(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    // Validate selected dates against GMB stats start date
    validateDateSelection() {
      if (!this.customStartDate || !this.gmbStatsStartDate) {
        return true; // Allow if no constraints
      }

      const selectedStart = new Date(this.customStartDate);
      const gmbStart = new Date(this.gmbStatsStartDate);

      if (selectedStart < gmbStart) {
        // Show user-friendly error message
        const formattedMinDate = new Date(
          this.gmbStatsStartDate
        ).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });

        alert(
          `Start date cannot be before ${formattedMinDate}. Please select a valid date.`
        );

        // Reset to minimum allowed date
        this.customStartDate = this.gmbStatsStartDate;
        return false;
      }

      return true;
    },

    async applyCustomDateRange() {
      if (!this.customStartDate || !this.customEndDate) {
        return;
      }

      // Validate dates before applying
      if (!this.validateDateSelection()) {
        return;
      }

      // Set loading states
      this.isLoadingDates = true;
      this.isChartLoading = true;
      this.isAggregatedDataLoading = true;

      // Calculate date range for custom dates
      const startDate = new Date(this.customStartDate);
      const endDate = new Date(this.customEndDate);

      // Calculate total days for label logic
      const totalDays =
        Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

      // Set custom date range data
      this.selectedDate = {
        isCustom: true,
        startDate: startDate,
        endDate: endDate,
        totalDays: totalDays,
        text: `${startDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })} - ${endDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}`,
      };

      this.isCustomDateRange = true;
      this.showCustomDateRange = false;

      // Reset data
      this.previousAggregatedData = {};
      this.previousMonthDataFetched = false;
      this.metricsData = [];
      this.chartData = [];

      // Don't store custom date range in vuex/localStorage
      // Always fetch fresh data for custom ranges
      await this.refreshDataForCustomRange();

      // Reset and reopen all dropdowns
      this.selectedWIDs.forEach((wid) => {
        this.$set(this.showPreviousData, wid, true);
      });
    },

    async refreshDataForCustomRange() {
      // Prevent multiple simultaneous API calls
      if (this.isCustomDataFetching) {
        console.log("Custom data fetch already in progress, skipping...");
        return;
      }

      try {
        this.isCustomDataFetching = true;
        // Fetch data without caching for custom date ranges
        await this.fetchDataForCustomRange();
        await this.aggregatedMetricsForCustomRange("current");

        // Don't fetch previous data for custom ranges >6 months as per requirements
        const totalDays = this.selectedDate.totalDays;
        if (totalDays <= 180) {
          // ~6 months (180 days)
          console.log(
            `Fetching previous data for custom range: ${totalDays} days <= 180 days`
          );
          await this.aggregatedMetricsForCustomRange("previous");
        } else {
          console.log(
            `Skipping previous data fetch for custom range: ${totalDays} days > 180 days`
          );
        }

        this.updatePaginatedWIDs();

        // Automatically fetch detailed previous metrics for all selected stores (for ranges <= 6 months)
        if (totalDays <= 180) {
          console.log(
            "Auto-fetching detailed previous metrics for all selected WIDs"
          );
          await this.fetchAllDetailedPreviousMetricsForCustomRange();
        }
      } catch (error) {
        console.error("Error refreshing data for custom range:", error);
        this.$snackbar.show({
          message: "Error fetching data for custom date range",
          color: "error",
        });
      } finally {
        this.isLoadingDates = false;
        this.isChartLoading = false;
        this.isAggregatedDataLoading = false;
        this.previousMonthAggregatedDataLoading = false;
        this.isCustomDataFetching = false;
      }
    },

    // Custom date range data fetching (no caching)
    async fetchDataForCustomRange() {
      try {
        if (this.selectedWIDs.length === 0) {
          this.isChartLoading = false;
          this.isLoading = false;
          return;
        }

        this.metricsData = [];
        this.chartData = [];
        this.allFetchedData = {};
        this.dataFetched = false;
        this.isLoading = true;

        let wids = this.selectedWIDs;
        const startDate = this.selectedDate.startDate;
        const endDate = this.selectedDate.endDate;

        const connectedAppsPromises = wids?.map(async (wid) => {
          const connectedAppsRef = collection(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            wid,
            "connected-apps"
          );
          const gbpDoc = await getDocs(
            query(
              connectedAppsRef,
              where("__name__", "==", "google_business_profile")
            )
          );
          return { wid, gbpDoc };
        });

        const payload = {
          uid: this.currentUID,
          wids: wids,
          start_date: this.formatDateForPayload(startDate),
          end_date: this.formatDateForPayload(endDate),
          type: "from_query",
          isCustomRange: true,
        };

        const connectedAppsResults = await Promise.all(connectedAppsPromises);

        connectedAppsResults.forEach(({ wid, gbpDoc }) => {
          if (gbpDoc.empty) return;

          const gbpData = gbpDoc.docs[0].data();
          const accounts = gbpData.accounts;
          if (!accounts?.length) return;

          const locations = accounts[0].locations;
          if (!locations?.length) return;

          const location = locations[0];

          this.locationTitlesMapping[wid] = {
            title: location.title,
            locationId: location.name,
            wid,
          };
        });

        const idToken = await auth.currentUser.getIdToken();
        const startTime = Date.now();
        let result;

        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/refreshGmbMetrics`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ data: payload }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const endTime = Date.now();
        const duration = endTime - startTime;
        console.log(`Custom range fetch time: ${duration} milliseconds`);
        const resultData = await response.json();

        if (
          !resultData ||
          !Array.isArray(resultData) ||
          resultData.length === 0
        ) {
          throw new Error("Invalid or empty response from server");
        }

        result = { data: resultData };
        console.log("Custom range fetchPlatformMetrics result:", result);
        this.dataFetched = result.data[0]?.status || false;

        result.data?.forEach((item) => {
          const detailedData = item?.data?.detailedData || [];

          detailedData.forEach((metric) => {
            if (!this.allFetchedData[metric.wid]) {
              this.allFetchedData[metric.wid] = {};
            }
            const dateKey = metric?.date?.value;
            if (!this.allFetchedData[metric.wid][dateKey]) {
              this.$set(this.allFetchedData[metric.wid], dateKey, {});
            }
            this.allFetchedData[metric.wid][dateKey] =
              this.extractMetrics(metric);

            // Don't store in vuex for custom ranges
          });
        });

        await this.updateMetricsAndChartData();
        this.isLoading = false;
        this.isChartLoading = false;
      } catch (error) {
        console.error("Error fetching custom range data:", error);
        this.isLoading = false;
        this.isChartLoading = false;
        throw error;
      }
    },

    async aggregatedMetricsForCustomRange(type) {
      try {
        if (this.selectedWIDs.length === 0) {
          return;
        }

        // Don't fetch previous data for ranges > 6 months
        if (type === "previous" && this.selectedDate.totalDays > 180) {
          return;
        }

        if (type === "current") {
          this.isAggregatedDataLoading = true;
        } else {
          this.previousMonthAggregatedDataLoading = true;
        }

        const startDate =
          type === "current"
            ? this.selectedDate.startDate
            : this.getPreviousRangeStart();
        const endDate =
          type === "current"
            ? this.selectedDate.endDate
            : this.getPreviousRangeEnd();

        console.log(`Custom ${type} date range:`, {
          startDate: this.formatDateForPayload(startDate),
          endDate: this.formatDateForPayload(endDate),
          totalDays:
            Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1,
        });

        let payload = {
          uid: this.currentUID,
          start: this.formatDateForPayload(startDate),
          end: this.formatDateForPayload(endDate),
          type: "aggregated_metrics",
          wids: this.selectedWIDs,
          isCustomRange: true,
        };

        console.log(
          `Making ${type} aggregated metrics API call with payload:`,
          payload
        );

        const idToken = await auth.currentUser.getIdToken();
        const startTime = Date.now();
        let result;

        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/refreshGmbMetrics`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ data: payload }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const endTime = Date.now();
        const duration = endTime - startTime;
        console.log(
          `Custom aggregated metrics (${type}) time: ${duration} milliseconds`
        );
        const resultData = await response.json();

        if (!resultData) {
          console.warn(
            `No data received for custom ${type} aggregated metrics`
          );
          result = { data: [] };
        } else {
          result = { data: resultData };
        }

        if (type === "current") {
          this.rawAggregatedMetrics = result?.data || [];
          this.computeAggregatedDataFromRaw("current");
        } else {
          this.previousRawAggregatedMetrics = result?.data || [];
          this.computeAggregatedDataFromRaw("previous");
        }

        // Don't store in vuex for custom ranges
      } catch (error) {
        console.error(
          `Error fetching custom ${type} aggregated metrics:`,
          error
        );
      } finally {
        if (type === "current") {
          this.isAggregatedDataLoading = false;
        } else {
          this.previousMonthAggregatedDataLoading = false;
        }
      }
    },

    getPreviousRangeStart() {
      // Get the local date components without timezone interference
      const currentStart = new Date(this.selectedDate.startDate);
      const currentEnd = new Date(this.selectedDate.endDate);

      // Create pure date objects (no time component) for accurate day calculations
      // This handles timezone issues by working with local date components
      const startDateOnly = new Date(
        currentStart.getFullYear(),
        currentStart.getMonth(),
        currentStart.getDate()
      );
      const endDateOnly = new Date(
        currentEnd.getFullYear(),
        currentEnd.getMonth(),
        currentEnd.getDate()
      );

      console.log("🔍 Pure date calculation debug:", {
        currentStartInput: this.selectedDate.startDate,
        currentEndInput: this.selectedDate.endDate,
        startDateOnly,
        endDateOnly,
        startDateOnlyString: startDateOnly.toISOString().split("T")[0],
        endDateOnlyString: endDateOnly.toISOString().split("T")[0],
      });

      // Calculate the number of days in the current range using safe method
      const rangeDays = this.safeDateDifference(startDateOnly, endDateOnly);

      // Validate range days to prevent invalid calculations
      if (rangeDays <= 0) {
        console.error("Invalid date range: end date must be after start date");
        return new Date(startDateOnly);
      }

      // Previous range ends the day before current range starts
      const previousEnd = this.safeSubtractDays(startDateOnly, 1);

      // Calculate previous range start
      // For a consistent previous period, we want same duration but shifted back
      let previousStart;

      // Handle edge case: if range is 1 day, previous should also be 1 day
      if (rangeDays === 1) {
        // For 1-day range, previous start = previous end
        previousStart = new Date(previousEnd);
      } else {
        // For multi-day ranges, calculate based on duration
        // Using rangeDays - 2 to get the correct start date (fix for off-by-one issue)
        previousStart = this.safeSubtractDays(previousEnd, rangeDays - 2);
      }

      // Validate the calculated range
      if (
        !this.validateDateCalculation(
          previousStart,
          previousEnd,
          "previous range calculation"
        )
      ) {
        console.warn("Invalid previous range calculated, using fallback...");
        // Fallback: ensure previous start is before previous end
        previousStart = this.safeSubtractDays(
          previousEnd,
          Math.max(rangeDays - 2, 0)
        );
      }

      // Calculate actual previous duration for validation
      const actualPreviousDuration = this.safeDateDifference(
        previousStart,
        previousEnd
      );

      console.log("🔍 Pure date calculation steps:", {
        rangeDays,
        previousEnd,
        previousEndString: previousEnd.toISOString().split("T")[0],
        daysToSubtract: rangeDays === 1 ? 0 : rangeDays - 2,
        previousStart,
        previousStartString: previousStart.toISOString().split("T")[0],
        rangeValidation: {
          currentDuration: rangeDays,
          previousDuration: actualPreviousDuration,
          isValidRange: previousStart <= previousEnd,
          durationsMatch:
            rangeDays === actualPreviousDuration ||
            (rangeDays === 1 && actualPreviousDuration === 1),
        },
        edgeCaseChecks: {
          isLeapYear:
            new Date(previousStart.getFullYear(), 1, 29).getDate() === 29,
          crossesMonthBoundary:
            previousStart.getMonth() !== previousEnd.getMonth(),
          crossesYearBoundary:
            previousStart.getFullYear() !== previousEnd.getFullYear(),
          startDayOfMonth: previousStart.getDate(),
          endDayOfMonth: previousEnd.getDate(),
        },
      });

      return previousStart;
    },

    getPreviousRangeEnd() {
      const currentStart = new Date(this.selectedDate.startDate);

      // Create pure date object (no time component) for timezone-safe calculations
      const startDateOnly = new Date(
        currentStart.getFullYear(),
        currentStart.getMonth(),
        currentStart.getDate()
      );

      // Previous range ends the day before current range starts
      const previousEnd = this.safeSubtractDays(startDateOnly, 1);

      // Additional validation for edge cases
      if (isNaN(previousEnd.getTime())) {
        console.error("Invalid previous end date calculation");
        // Fallback: return yesterday relative to current start using native method
        const fallback = new Date(startDateOnly);
        fallback.setDate(fallback.getDate() - 1);
        return fallback;
      }

      return previousEnd;
    },

    // Helper method to validate date calculations for edge cases
    validateDateCalculation(startDate, endDate, context = "date calculation") {
      // Check for invalid dates
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.error(`Invalid dates in ${context}:`, { startDate, endDate });
        return false;
      }

      // Check for logical order
      if (startDate > endDate) {
        console.error(`Start date after end date in ${context}:`, {
          startDate,
          endDate,
        });
        return false;
      }

      // Check for extremely large ranges (> 2 years as safety check)
      const daysDiff = Math.abs((endDate - startDate) / (1000 * 60 * 60 * 24));
      if (daysDiff > 730) {
        console.warn(
          `Very large date range detected in ${context}: ${daysDiff} days`
        );
      }

      return true;
    },

    // Helper method to safely calculate date differences
    safeDateDifference(startDate, endDate) {
      try {
        const timeDiff = endDate.getTime() - startDate.getTime();
        const daysDiff = Math.round(timeDiff / (1000 * 60 * 60 * 24));
        return daysDiff + 1; // +1 for inclusive counting
      } catch (error) {
        console.error("Error calculating date difference:", error);
        return 1; // Default to 1 day
      }
    },

    // Helper method to safely subtract days from a date
    safeSubtractDays(date, days) {
      try {
        const result = new Date(date);
        result.setDate(result.getDate() - days);

        // Validate the result
        if (isNaN(result.getTime())) {
          console.error("Invalid date result from subtraction:", {
            originalDate: date,
            days,
          });
          return new Date(date); // Return original date as fallback
        }

        return result;
      } catch (error) {
        console.error("Error subtracting days:", error);
        return new Date(date);
      }
    },

    // Helper method to select evenly distributed data points
    selectEvenly(data, targetCount) {
      if (data.length <= targetCount) {
        return data;
      }

      const selectedPoints = [];
      const step = (data.length - 1) / (targetCount - 1);

      for (let i = 0; i < targetCount; i++) {
        let index;
        if (i === 0) {
          index = 0; // Always include first point
        } else if (i === targetCount - 1) {
          index = data.length - 1; // Always include last point
        } else {
          index = Math.round(i * step);
        }

        // Ensure we don't duplicate points
        if (
          selectedPoints.length === 0 ||
          selectedPoints[selectedPoints.length - 1] !== data[index]
        ) {
          selectedPoints.push(data[index]);
        }
      }

      return selectedPoints;
    },

    async fetchAllDetailedPreviousMetricsForCustomRange() {
      console.log(
        "Fetching detailed previous metrics for all selected WIDs:",
        this.selectedWIDs
      );

      // Fetch detailed previous metrics for all selected stores in parallel
      const fetchPromises = this.selectedWIDs.map((wid) =>
        this.fetchPreviousMetricsForCustomRange(wid)
      );

      try {
        await Promise.all(fetchPromises);
        console.log(
          "Completed fetching detailed previous metrics for all WIDs"
        );
        console.log("Final previousMetrics object:", this.previousMetrics);

        // Force a UI update to show the percentage changes
        this.$forceUpdate();

        // Also trigger reactive updates
        this.$nextTick(() => {
          console.log(
            "UI should now show percentage changes for custom date range"
          );
        });
      } catch (error) {
        console.error(
          "Error fetching detailed previous metrics for some WIDs:",
          error
        );
      }
    },

    async fetchPreviousMetricsForCustomRange(wid) {
      try {
        // reset previous metrics for this wid
        this.$set(this.previousMetrics, wid, {});
        console.log(`Fetching previous metrics for custom range - WID: ${wid}`);

        // Don't fetch previous data for custom ranges > 6 months
        if (this.selectedDate.totalDays > 180) {
          console.log(
            `Skipping previous metrics for custom range > 180 days for WID: ${wid}`
          );
          return;
        }

        // Calculate previous date range for this custom range
        const previousStart = this.getPreviousRangeStart();
        const previousEnd = this.getPreviousRangeEnd();

        console.log(`Previous range for WID ${wid}:`, {
          start: this.formatDateForPayload(previousStart),
          end: this.formatDateForPayload(previousEnd),
        });

        // Get connected apps data for this specific WID
        const connectedAppsRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          wid,
          "connected-apps"
        );

        const gbpDoc = await getDocs(
          query(
            connectedAppsRef,
            where("__name__", "==", "google_business_profile")
          )
        );

        if (gbpDoc.empty) {
          console.warn(`No GMB data found for WID: ${wid}`);
          return;
        }

        const gbpData = gbpDoc.docs[0].data();
        const accounts = gbpData.accounts;
        if (!accounts?.length) {
          console.warn(`No accounts found for WID: ${wid}`);
          return;
        }

        const locations = accounts[0].locations;
        if (!locations?.length) {
          console.warn(`No locations found for WID: ${wid}`);
          return;
        }

        const location = locations[0];
        const accountId = accounts[0].name;

        const payload = {
          uid: this.currentUID,
          wid,
          platform: "google_business_profile",
          account: accountId,
          location: location.name,
          dateRange: {
            start: this.formatDateForPayload(previousStart),
            end: this.formatDateForPayload(previousEnd),
          },
          resource_type: "detailed",
          type: "previous_months",
          isCustomRange: true,
        };

        console.log(
          `Making custom previous metrics API call for WID ${wid}:`,
          payload
        );

        const idToken = await auth.currentUser.getIdToken();
        const startTime = Date.now();

        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/fetchPlatformMetrics`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ data: payload }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const endTime = Date.now();
        const duration = endTime - startTime;
        console.log(
          `Custom previous metrics fetch time for WID ${wid}: ${duration} milliseconds`
        );

        const result = { data: await response.json() };
        console.log(`Custom previous metrics result for WID ${wid}:`, result);

        if (result && result.data && result.data.length > 0) {
          // Check the actual structure of the response
          console.log(`Raw API response structure for WID ${wid}:`, {
            resultDataLength: result.data.length,
            firstItem: result.data[0],
            hasDetailedData: result.data[0]?.detailedData ? "Yes" : "No",
          });
          const metricsData = result.data[0];

          const metrics = this.extractMetrics(metricsData);

          if (Object.values(metrics).some((value) => value > 0)) {
            this.$set(this.previousMetrics, wid, metrics);
          } else {
            const emptyMetrics = {
              desktop_maps_impressions: 0,
              mobile_maps_impressions: 0,
              desktop_search_impressions: 0,
              mobile_search_impressions: 0,
              direction_requests: 0,
              call_clicks: 0,
              website_clicks: 0,
              bookings: 0,
              food_orders: 0,
              food_menu_clicks: 0,
            };
            this.$set(this.previousMetrics, wid, emptyMetrics);
          }
          // Process and aggregate the detailed data for this WID
          const aggregatedPreviousData =
            this.aggregateDetailedDataForCustomRange(result.data);

          console.log(
            `Aggregated previous data for WID ${wid}:`,
            aggregatedPreviousData
          );

          // Store the previous metrics for this WID
          // this.$set(this.previousMetrics, wid, {
          //   ...aggregatedPreviousData,
          //   lastFetchedDate: new Date().toLocaleDateString("en-GB"),
          // });

          console.log(
            `Stored previous metrics for WID ${wid}:`,
            this.previousMetrics[wid]
          );

          // Also log the entire previousMetrics object for debugging
          console.log(
            "All previousMetrics after storing:",
            JSON.stringify(this.previousMetrics, null, 2)
          );
        } else {
          console.warn(`No previous data found for custom range - WID: ${wid}`);
        }
      } catch (error) {
        console.error(
          `Error fetching custom previous metrics for WID ${wid}:`,
          error
        );
      }
    },

    // Helper method to aggregate detailed data for custom ranges
    aggregateDetailedDataForCustomRange(apiResponseData) {
      const aggregated = {
        desktop_maps_impressions: 0,
        mobile_maps_impressions: 0,
        desktop_search_impressions: 0,
        mobile_search_impressions: 0,
        direction_requests: 0,
        call_clicks: 0,
        website_clicks: 0,
        bookings: 0,
        food_orders: 0,
        food_menu_clicks: 0,
      };

      console.log("Processing API response data:", apiResponseData);

      if (!apiResponseData || !Array.isArray(apiResponseData)) {
        console.warn("Invalid API response data structure");
        return aggregated;
      }

      apiResponseData.forEach((item, index) => {
        console.log(`Processing item ${index}:`, item);

        // Handle different possible structures
        let detailedData = null;

        if (item.data && item.data.detailedData) {
          detailedData = item.data.detailedData;
        } else if (item.detailedData) {
          detailedData = item.detailedData;
        } else if (Array.isArray(item)) {
          detailedData = item;
        }

        if (detailedData && Array.isArray(detailedData)) {
          console.log(
            `Found detailed data array with ${detailedData.length} items`
          );

          detailedData.forEach((metric, metricIndex) => {
            console.log(`Processing metric ${metricIndex}:`, metric);

            aggregated.desktop_maps_impressions +=
              parseInt(metric.desktop_maps_impressions) || 0;
            aggregated.mobile_maps_impressions +=
              parseInt(metric.mobile_maps_impressions) || 0;
            aggregated.desktop_search_impressions +=
              parseInt(metric.desktop_search_impressions) || 0;
            aggregated.mobile_search_impressions +=
              parseInt(metric.mobile_search_impressions) || 0;
            aggregated.direction_requests +=
              parseInt(metric.direction_requests) || 0;
            aggregated.call_clicks += parseInt(metric.call_clicks) || 0;
            aggregated.website_clicks += parseInt(metric.website_clicks) || 0;
            aggregated.bookings += parseInt(metric.bookings) || 0;
            aggregated.food_orders += parseInt(metric.food_orders) || 0;
            aggregated.food_menu_clicks +=
              parseInt(metric.food_menu_clicks) || 0;
          });
        } else {
          console.warn(`No detailed data found in item ${index}:`, item);
        }
      });

      console.log("Final aggregated data:", aggregated);
      return aggregated;
    },

    hasMetrics(metrics) {
      return Object.keys(metrics || {}).some(
        (key) =>
          metrics[key] !== undefined &&
          [
            "desktop_maps_impressions",
            "mobile_maps_impressions",
            "desktop_search_impressions",
            "mobile_search_impressions",
            "direction_requests",
            "call_clicks",
            "website_clicks",
            "bookings",
            "food_orders",
            "food_menu_clicks",
          ].includes(key)
      );
    },
    hasMetricsNew(metrics) {
      // Check if metrics is an array and has elements
      if (!Array.isArray(metrics) || metrics.length === 0) {
        return false;
      }

      // Check each metric object for the required keys
      return metrics.some((metric) =>
        [
          "desktop_maps_impressions",
          "mobile_maps_impressions",
          "desktop_search_impressions",
          "mobile_search_impressions",
          "direction_requests",
          "call_clicks",
          "website_clicks",
          "bookings",
          "food_orders",
          "food_menu_clicks",
        ].some((key) => metric[key] !== undefined)
      );
    },
    extractMetrics(metrics) {
      // console.log("Extracting metrics from:", metrics);
      return {
        desktop_maps_impressions:
          parseInt(metrics?.desktop_maps_impressions) || 0,
        mobile_maps_impressions:
          parseInt(metrics?.mobile_maps_impressions) || 0,
        desktop_search_impressions:
          parseInt(metrics?.desktop_search_impressions) || 0,
        mobile_search_impressions:
          parseInt(metrics?.mobile_search_impressions) || 0,
        direction_requests: parseInt(metrics?.direction_requests) || 0,
        call_clicks: parseInt(metrics?.call_clicks) || 0,
        website_clicks: parseInt(metrics?.website_clicks) || 0,
        bookings: parseInt(metrics?.bookings) || 0,
        food_orders: parseInt(metrics?.food_orders) || 0,
        food_menu_clicks: parseInt(metrics?.food_menu_clicks) || 0,
      };
    },
    async togglePreviousData(wid) {
      // console.log("togglePreviousData called for wid:", wid);
      this.$set(this.showPreviousData, wid, !this.showPreviousData[wid]);

      if (this.showPreviousData[wid] && !this.previousMetrics[wid]) {
        // console.log("Fetching previous metrics for wid:", wid);
        await this.fetchPreviousMetrics(wid);
      } else {
        console.log("Previous metrics already loaded or dropdown closed");
      }
    },

    async fetchPreviousMetrics(wid) {
      try {
        // console.log("Starting fetch Previous Metrics for wid:", wid);

        // Get the selected months from the current date range
        const selectedMonths = this.selectedDate?.months;
        // console.log("Selected months:", selectedMonths);
        // Check if we have valid cached data from today
        const cachedData =
          this.previousMonthMetricsInStore[wid]?.[selectedMonths];
        // console.log('cachedData:', cachedData)
        const today = new Date().toLocaleDateString("en-GB");
        if (cachedData && cachedData.lastFetchedDate === today) {
          this.$set(this.previousMetrics, wid, cachedData);
          return;
        }

        // For custom date ranges, use different method to fetch previous metrics
        if (this.selectedDate && this.selectedDate.isCustom) {
          await this.fetchPreviousMetricsForCustomRange(wid);
          return;
        }

        // Don't fetch previous data for >6 months (last 1 year)
        if (this.selectedDate && this.selectedDate.months === 12) {
          console.warn("Skipping previous metrics fetch for 12 months");
          return;
        }

        if (!selectedMonths) {
          console.warn("No months selected");
          return;
        }

        const dateRanges = this.calculateDateRanges(selectedMonths);

        const connectedAppsRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          wid,
          "connected-apps"
        );

        const gbpDoc = await getDocs(
          query(
            connectedAppsRef,
            where("__name__", "==", "google_business_profile")
          )
        );

        if (!gbpDoc.empty) {
          const gbpData = gbpDoc.docs[0].data();
          const accounts = gbpData.accounts;

          if (accounts?.length) {
            const accountId = accounts[0].id;
            const location = accounts[0].locations?.[0];

            if (location) {
              const payload = {
                uid: this.currentUID,
                wid,
                platform: "google_business_profile",
                account: accountId,
                location: location.name,
                dateRange: {
                  start: this.formatDateForPayload(dateRanges.previous.start),
                  end: this.formatDateForPayload(dateRanges.previous.end),
                },
                resource_type: "detailed",
                type: "previous_months",
                months: selectedMonths,
              };

              const idToken = await auth.currentUser.getIdToken();
              // check how much time it tokk to get the response
              const startTime = Date.now();
              let result;
              const response = await fetch(
                `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/fetchPlatformMetrics`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${idToken}`,
                  },
                  body: JSON.stringify({ data: payload }),
                }
              );
              if (response.ok) {
                const endTime = Date.now();
                const duration = endTime - startTime;
                console.log(`Time taken: ${duration} milliseconds`);
                const resultData = await response.json();
                result = { data: resultData };
                // console.log("Success! Data from server fetchPreviousMetrics:", result);
              }
              // console.log("Raw API response:", result);
              if (result.data && result.data.length > 0) {
                const metricsData = result.data[0];

                const metrics = this.extractMetrics(metricsData);
                // console.log("Extracted metrics:", metrics);

                if (Object.values(metrics).some((value) => value > 0)) {
                  this.$set(this.previousMetrics, wid, metrics);
                  // Persist in store
                  this.$store.commit("SET_PREVIOUS_MONTH_METRICS", {
                    wid,
                    monthSelection: selectedMonths,
                    data: metrics,
                  });
                } else {
                  const emptyMetrics = {
                    desktop_maps_impressions: 0,
                    mobile_maps_impressions: 0,
                    desktop_search_impressions: 0,
                    mobile_search_impressions: 0,
                    direction_requests: 0,
                    call_clicks: 0,
                    website_clicks: 0,
                    bookings: 0,
                    food_orders: 0,
                    food_menu_clicks: 0,
                  };
                  this.$set(this.previousMetrics, wid, emptyMetrics);
                  // Persist empty metrics
                  this.$store.commit("SET_PREVIOUS_MONTH_METRICS", {
                    wid,
                    monthSelection: selectedMonths,
                    data: emptyMetrics,
                  });
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching previous metrics:", error);
        this.$set(this.previousMetrics, wid, {
          desktop_maps_impressions: 0,
          mobile_maps_impressions: 0,
          desktop_search_impressions: 0,
          mobile_search_impressions: 0,
          direction_requests: 0,
          call_clicks: 0,
          website_clicks: 0,
          bookings: 0,
          food_orders: 0,
          food_menu_clicks: 0,
        });
      }
    },
    calculatePercentageChange(current, previous) {
      // If both values are 0, return 0%
      if (current === 0 && previous === 0) return 0;

      // If previous is 0 and current is not, return 100%
      if (previous === 0 && current > 0) return 100;

      // If current is 0 and previous is not, return -100%
      if (current === 0 && previous > 0) return -100;

      // Calculate percentage change
      const change = ((current - previous) / previous) * 100;

      // Round to whole number
      return Math.round(change);
    },
    getPercentageIcon(current, previous) {
      // If both values are 0, show minus
      if (current === 0 && previous === 0) return "mdi-minus";

      // If there's any change, show appropriate arrow
      if (current > previous) return "mdi-arrow-up";
      if (current < previous) return "mdi-arrow-down";
      return "mdi-minus";
    },
    getPercentageClass(current, previous) {
      // If both values are 0, use gray
      if (current === 0 && previous === 0) return "text-gray-500";

      // If there's any change, use appropriate color
      if (current > previous) return "text-green-500";
      if (current < previous) return "text-red-500";
      return "text-gray-500";
    },
    handleConnectGMB() {
      try {
        this.$snackbar.show("Please wait a moment");
        this.isLoading = true;

        // Use the initConnection method from the connected.apps.mixins.js
        this.initConnection("google_business_profile", "bulk");

        setTimeout(() => {
          this.$snackbar.hide();
        }, 2000);
      } catch (error) {
        console.error("Error connecting Google Business Profile:", error);
        this.$alert.show(
          "Failed to start Google Business connection. Please try again."
        );
      } finally {
        this.isLoading = false;
      }
    },
    getBestPerformingDay() {
      if (!this.chartData || !this.chartData.length) return "N/A";

      const dayStats = {};
      this.chartData.forEach((data) => {
        const date = new Date(data.date);
        const day = date.toLocaleDateString("en-US", { weekday: "long" });
        const totalImpressions =
          (data.desktop_maps_impressions || 0) +
          (data.mobile_maps_impressions || 0) +
          (data.desktop_search_impressions || 0) +
          (data.mobile_search_impressions || 0);

        if (!dayStats[day]) {
          dayStats[day] = { total: 0, count: 0 };
        }
        dayStats[day].total += totalImpressions;
        dayStats[day].count++;
      });

      let bestDay = "";
      let bestAverage = 0;
      Object.entries(dayStats).forEach(([day, stats]) => {
        const average = stats.total / stats.count;
        if (average > bestAverage) {
          bestDay = day;
          bestAverage = average;
        }
      });

      return bestDay || "N/A";
    },

    getPeakSearchTime() {
      if (!this.chartData || !this.chartData.length) return "N/A";

      const timeStats = {};
      this.chartData.forEach((data) => {
        const date = new Date(data.date);
        const hour = date.getHours();
        const totalSearches =
          (data.desktop_search_impressions || 0) +
          (data.mobile_search_impressions || 0);

        if (!timeStats[hour]) {
          timeStats[hour] = { total: 0, count: 0 };
        }
        timeStats[hour].total += totalSearches;
        timeStats[hour].count++;
      });

      let peakHour = 0;
      let peakAverage = 0;
      Object.entries(timeStats).forEach(([hour, stats]) => {
        const average = stats.total / stats.count;
        if (average > peakAverage) {
          peakHour = parseInt(hour);
          peakAverage = average;
        }
      });

      return `${peakHour % 12 || 12}${peakHour < 12 ? "AM" : "PM"}`;
    },

    getSearchGrowth() {
      if (!this.chartData || !this.chartData.length) return 0;

      const firstWeek = this.chartData.slice(0, 7);
      const lastWeek = this.chartData.slice(-7);

      const firstWeekTotal = firstWeek.reduce((sum, data) => {
        return (
          sum +
          (data.desktop_search_impressions || 0) +
          (data.mobile_search_impressions || 0)
        );
      }, 0);

      const lastWeekTotal = lastWeek.reduce((sum, data) => {
        return (
          sum +
          (data.desktop_search_impressions || 0) +
          (data.mobile_search_impressions || 0)
        );
      }, 0);

      if (firstWeekTotal === 0) return 0;
      return Math.round(
        ((lastWeekTotal - firstWeekTotal) / firstWeekTotal) * 100
      );
    },

    getActionGrowth() {
      if (!this.chartData || !this.chartData.length) return 0;

      const firstWeek = this.chartData.slice(0, 7);
      const lastWeek = this.chartData.slice(-7);

      const getActionTotal = (data) => {
        return (
          (data.direction_requests || 0) +
          (data.call_clicks || 0) +
          (data.website_clicks || 0) +
          (data.bookings || 0)
        );
      };

      const firstWeekTotal = firstWeek.reduce(
        (sum, data) => sum + getActionTotal(data),
        0
      );
      const lastWeekTotal = lastWeek.reduce(
        (sum, data) => sum + getActionTotal(data),
        0
      );

      if (firstWeekTotal === 0) return 0;
      return Math.round(
        ((lastWeekTotal - firstWeekTotal) / firstWeekTotal) * 100
      );
    },

    getGrowthClass(value) {
      if (value > 0) return "text-green-500";
      if (value < 0) return "text-red-500";
      return "text-gray-500";
    },

    getGrowthColor(value) {
      if (value > 0) return "success";
      if (value < 0) return "error";
      return "gray";
    },

    getGrowthIcon(value) {
      if (value > 0) return "mdi-arrow-up";
      if (value < 0) return "mdi-arrow-down";
      return "mdi-minus";
    },

    getTopPerformingLocations() {
      if (!this.metricsData || !this.metricsData.length) {
        return [];
      }

      // Map locations with their scores
      const locationScores = this.metricsData.map((metric) => {
        const totalScore =
          (metric.desktop_maps_impressions || 0) +
          (metric.mobile_maps_impressions || 0) +
          (metric.desktop_search_impressions || 0) +
          (metric.mobile_search_impressions || 0);

        return {
          name:
            metric.location?.short_address ||
            metric.location?.name ||
            metric.location?.title ||
            "Unknown Location",
          score: totalScore,
        };
      });

      // Sort locations by score in descending order and get top 5
      return locationScores
        .sort((a, b) => b.score - a.score)
        .slice(0, 5)
        .filter((location) => location.score > 0); // Only include locations with scores > 0
    },

    getBottomPerformingLocations() {
      if (!this.metricsData || !this.metricsData.length) {
        return [];
      }

      // Map locations with their scores
      const locationScores = this.metricsData.map((metric) => {
        const totalScore =
          (metric.desktop_maps_impressions || 0) +
          (metric.mobile_maps_impressions || 0) +
          (metric.desktop_search_impressions || 0) +
          (metric.mobile_search_impressions || 0);

        return {
          name:
            metric.location?.short_address ||
            metric.location?.name ||
            metric.location?.title ||
            "Unknown Location",
          score: totalScore,
        };
      });

      // Sort locations by score in ascending order and get bottom 5
      return locationScores
        .sort((a, b) => a.score - b.score) // Sort ascending for bottom performers
        .filter((location) => location.score > 0) // Only include locations with scores > 0
        .slice(0, 5); // Get bottom 5
    },

    getMostImprovedLocation() {
      if (
        !this.metricsData ||
        !this.metricsData.length ||
        !this.previousMetrics
      )
        return "N/A";

      let mostImprovedLocation = null;
      let highestGrowth = 0;

      this.metricsData.forEach((metric) => {
        const prevMetric = this.previousMetrics[metric.location?.wid];
        if (!prevMetric) return;

        const currentTotal =
          (metric.desktop_maps_impressions || 0) +
          (metric.mobile_maps_impressions || 0) +
          (metric.desktop_search_impressions || 0) +
          (metric.mobile_search_impressions || 0);

        const previousTotal =
          (prevMetric.desktop_maps_impressions || 0) +
          (prevMetric.mobile_maps_impressions || 0) +
          (prevMetric.desktop_search_impressions || 0) +
          (prevMetric.mobile_search_impressions || 0);

        if (previousTotal === 0) return;

        const growth = ((currentTotal - previousTotal) / previousTotal) * 100;
        if (growth > highestGrowth) {
          highestGrowth = growth;
          mostImprovedLocation = metric.location?.title || "Unknown Location";
        }
      });

      return mostImprovedLocation || "N/A";
    },

    getMostCommonAction() {
      if (!this.aggregatedData) return "N/A";

      const actions = [
        {
          name: "Direction Requests",
          value: this.aggregatedData.total_direction_requests || 0,
        },
        {
          name: "Call Clicks",
          value: this.aggregatedData.total_call_clicks || 0,
        },
        {
          name: "Website Clicks",
          value: this.aggregatedData.total_website_clicks || 0,
        },
        { name: "Bookings", value: this.aggregatedData.total_bookings || 0 },
      ];

      const mostCommon = actions.reduce((prev, current) =>
        prev.value > current.value ? prev : current
      );

      return mostCommon.name;
    },

    getMobileVsDesktopRatio() {
      if (!this.aggregatedData) return "N/A";

      const mobileTotal =
        (this.aggregatedData.total_mobile_maps_impressions || 0) +
        (this.aggregatedData.total_mobile_search_impressions || 0);
      const desktopTotal =
        (this.aggregatedData.total_desktop_maps_impressions || 0) +
        (this.aggregatedData.total_desktop_search_impressions || 0);

      if (desktopTotal === 0) return "100% Mobile";
      if (mobileTotal === 0) return "100% Desktop";

      const mobilePercentage = Math.round(
        (mobileTotal / (mobileTotal + desktopTotal)) * 100
      );
      return `${mobilePercentage}% Mobile`;
    },
  },

  async mounted() {
    this.fetchWorkspaceData();
    this.fetchGMBLogo();
    await this.fetchGmbStatsStartDate(); // Fetch GMB stats start date for date blocking
    this.isChartLoading = true;
    this.isLoading = true;
    await this.fetchStores();
    // Only fetch stats if there are selected locations
    if (this.selectedWIDs.length > 0) {
      // Run both aggregatedMetrics calls in parallel
      await Promise.all([
        this.aggregatedMetrics(),
        this.aggregatedMetrics("previous"),
      ]);
      this.isLoading = false;
      this.refreshData();
      // Fetch metrics for all initially selected stores
    } else {
      // No locations, so stop loading indicators
      this.isLoading = false;
      this.isChartLoading = false;
    }
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    this.$loader.hide();
    document.removeEventListener("click", this.handleClickOutside);
    this.destroyChart("metricsChart");
    this.destroyChart("mobileMetricsChart");
    this.destroyChart("desktopMetricsChart");
  },
};
</script>

<style scoped>
.text-xs {
  font-size: 0.75rem;
}

:deep(.v-text-field.location-input) {
  .v-input__slot {
    min-height: 40px !important;
  }

  .v-text-field__placeholder {
    color: rgba(0, 0, 0, 0.6);
    font-size: 14px;
  }

  input {
    font-size: 14px;
  }

  fieldset {
    border-color: rgba(0, 0, 0, 0.6) !important;
  }
}

.text-center {
  text-align: center;
}

img {
  object-fit: cover;
}

.cursor-pointer {
  cursor: pointer;
}

.hover\:bg-gray-100:hover {
  background-color: #f7fafc;
}

.leading-tight {
  line-height: 1.25;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
}

.skeleton-loader {
  display: inline-block;
  width: 50px;
  height: 20px;
  background-color: #e0e0e0;
  border-radius: 4px;
  animation: skeleton-loading 1.5s infinite;
}

.overflow-x-auto {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

/* Optional: Custom scrollbar styling */
.overflow-x-auto::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.tooltip-container {
  position: relative;
  z-index: 40;
}

.v-tooltip__content {
  z-index: 60 !important;
  position: fixed !important;
}

.v-icon {
  z-index: 30;
  position: relative;
}

.showStoreDropdown {
  background-color: white;
  /* Ensure solid background */
  z-index: 50;
  position: relative;
}

.date-select {
  position: relative;
  z-index: 40;
}

button.tabs {
  border-width: 2px;
  border-style: solid;
  border-color: #8056de;
}

button.tabs.active {
  background-color: #8056de;
  color: white;
}

.metrics-select {
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.no-left-margin :deep(.v-slide-group__wrapper) {
  margin-left: 0;
  padding-left: 0;
}

.no-left-margin :deep(.v-tabs-slider-wrapper) {
  margin-left: 0;
}
/* Ensure v-select layout stability for screenshot */
.date-select :deep(.v-select__selection) {
  white-space: nowrap; /* Prevent text wrapping */
  overflow: visible; /* Ensure text isn't cut off */
}

.date-select :deep(.v-input__slot) {
  min-height: 40px !important; /* Consistent height */
  display: flex !important; /* Align items properly */
  align-items: center !important;
}

/* Raise z-index to avoid overlap */
.date-select {
  position: relative;
  overflow: visible;
  z-index: 100 !important; /* Above other elements */
}
/* This ensures the v-select dropdown menu appears above everything */
:deep(.v-menu__content) {
  z-index: 60 !important;
}
.group:hover .group-hover\:block {
  display: block !important;
}
/* Screenshot Header Styles */
.screenshot-header {
  margin: -1rem -1rem 1.5rem -1rem; /* Negative margins to extend to container edges */
  position: relative;
  overflow: hidden;
}

.screenshot-header.hidden {
  display: none;
}

.maze-pattern {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  padding: 1.5rem 2rem;
  overflow: hidden;
}

.maze-pattern::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    /* Horizontal lines */ linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.1) 1px,
      transparent 1px
    ),
    /* Vertical lines */
      linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
    /* Diagonal cross pattern */
      linear-gradient(
        45deg,
        transparent 35%,
        rgba(255, 255, 255, 0.05) 35%,
        rgba(255, 255, 255, 0.05) 65%,
        transparent 65%
      ),
    /* Plus symbols */
      radial-gradient(
        circle at 25% 25%,
        rgba(255, 255, 255, 0.1) 2px,
        transparent 3px
      ),
    radial-gradient(
      circle at 75% 75%,
      rgba(255, 255, 255, 0.08) 2px,
      transparent 3px
    );
  background-size: 20px 20px, 20px 20px, 40px 40px, 80px 80px, 120px 120px;
  animation: mazeFloat 20s linear infinite;
}

@keyframes mazeFloat {
  0% {
    transform: translateX(0px);
  }
  100% {
    transform: translateX(40px);
  }
}

.header-content {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.workspace-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.workspace-logo {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.workspace-name {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transform: translateY(-10px);
}

.fly-text {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.5rem;
  font-weight: 300;
  font-style: italic;
  margin-left: 0.5rem;
}

.date-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  position: relative;
  padding-right: 4rem;
  transform: translateY(-10px);
}

.date-text::after {
  content: "";
  position: absolute;
  right: -3.5rem;
  top: -20px;
  width: 60px;
  height: 80px;
  background-image: 
    /* Row 1 - Top bubbles */ radial-gradient(
      circle at 10px 8px,
      rgba(255, 255, 255, 0.4) 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 25px 5px,
      rgba(255, 255, 255, 0.3) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 40px 10px,
      rgba(255, 255, 255, 0.35) 2.5px,
      transparent 3px
    ),
    radial-gradient(
      circle at 55px 7px,
      rgba(255, 255, 255, 0.25) 1px,
      transparent 2px
    ),
    /* Row 2 */
      radial-gradient(
        circle at 8px 20px,
        rgba(255, 255, 255, 0.3) 1.5px,
        transparent 2px
      ),
    radial-gradient(
      circle at 22px 18px,
      rgba(255, 255, 255, 0.4) 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 35px 22px,
      rgba(255, 255, 255, 0.2) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 48px 19px,
      rgba(255, 255, 255, 0.35) 2px,
      transparent 3px
    ),
    /* Row 3 - Middle */
      radial-gradient(
        circle at 12px 32px,
        rgba(255, 255, 255, 0.25) 2px,
        transparent 3px
      ),
    radial-gradient(
      circle at 28px 30px,
      rgba(255, 255, 255, 0.2) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 42px 34px,
      rgba(255, 255, 255, 0.3) 2.5px,
      transparent 3px
    ),
    radial-gradient(
      circle at 52px 31px,
      rgba(255, 255, 255, 0.15) 1px,
      transparent 2px
    ),
    /* Row 4 */
      radial-gradient(
        circle at 6px 44px,
        rgba(255, 255, 255, 0.2) 1px,
        transparent 2px
      ),
    radial-gradient(
      circle at 20px 42px,
      rgba(255, 255, 255, 0.25) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 36px 46px,
      rgba(255, 255, 255, 0.15) 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 50px 43px,
      rgba(255, 255, 255, 0.2) 1.5px,
      transparent 2px
    ),
    /* Row 5 - Bottom */
      radial-gradient(
        circle at 15px 56px,
        rgba(255, 255, 255, 0.15) 1px,
        transparent 2px
      ),
    radial-gradient(
      circle at 30px 54px,
      rgba(255, 255, 255, 0.1) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 45px 58px,
      rgba(255, 255, 255, 0.12) 1px,
      transparent 2px
    ),
    /* Additional scattered bubbles */
      radial-gradient(
        circle at 18px 68px,
        rgba(255, 255, 255, 0.08) 1px,
        transparent 2px
      ),
    radial-gradient(
      circle at 38px 70px,
      rgba(255, 255, 255, 0.06) 1px,
      transparent 2px
    );
  background-size: 60px 80px;
}

.date-text::before {
  content: "";
  position: absolute;
  right: -3rem;
  top: -15px;
  width: 50px;
  height: 70px;
  background-image: 
    /* Left side additional bubbles */ radial-gradient(
      circle at 5px 12px,
      rgba(255, 255, 255, 0.2) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 15px 8px,
      rgba(255, 255, 255, 0.15) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 8px 25px,
      rgba(255, 255, 255, 0.18) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 18px 28px,
      rgba(255, 255, 255, 0.12) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 12px 40px,
      rgba(255, 255, 255, 0.1) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 22px 45px,
      rgba(254, 45, 45, 0.08) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 6px 55px,
      rgba(255, 255, 255, 0.06) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 16px 60px,
      rgba(255, 255, 255, 0.05) 1px,
      transparent 2px
    );
  background-size: 50px 70px;
}

/* Custom date range dialog styles - matching GmbReviews.vue */
</style>
